import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ApiError,
  assignThreadContact,
  type CalendarImportResultDto,
  type CalendarUnmatchedItem,
  clearNoReply,
  commitCalendarImport,
  confirmCalendarAlias,
  confirmCandidate,
  type FollowupIncludeReason,
  getCalendarConfig,
  getCalendarStats,
  getChannelHealth,
  getConnectConfig,
  getEngageConfig,
  getEngagementInterests,
  getHygieneSummary,
  getInvitationsConfig,
  getJobChangeConfig,
  getKeepInTouchConfig,
  getLearningMetrics,
  getSendConfig,
  getStyleGuide,
  getThread,
  getThreadsSummary,
  listCandidates,
  listContacts,
  listFollowups,
  listPasskeys,
  listThreads,
  passkeySupported,
  patchContactProfileFacts,
  previewCalendarImport,
  putEngagementInterests,
  putStyleGuide,
  registerPasskey,
  rejectCandidate,
  scanForDuplicates,
  serverErrorMessage,
  setContactArchived,
  setContactMuted,
  setThreadMuted,
  snoozeThread,
} from '../lib/api.js';
import { AUTH_MODE } from '../lib/auth-config.js';
import type { NavView } from '../lib/destinations.js';
import { DESTINATIONS } from '../lib/destinations.js';
import { formatChannel, formatRelative, threadLabel } from '../lib/format.js';
import type { DrawerSection } from '../lib/health-status.js';
import { isEditableTarget } from '../lib/keyboard.js';
import { LIVE_POLL_MS } from '../lib/poll.js';
import {
  decideChime,
  type InboundClocks,
  playChime,
  primeAudio,
  setSoundEnabled,
  toMs,
  useSoundEnabled,
} from '../lib/sound.js';
import { useUrlParam } from '../lib/url-state.js';
import { useDebouncedValue } from '../lib/use-debounced-value.js';
import { useWorkSummary } from '../lib/use-work-summary.js';
import type {
  CandidateDto,
  FollowupListItemDto,
  FollowupReason,
  ThreadDetailDto,
  ThreadListItemDto,
} from '../types.js';
import { ContactPicker } from './AddressBook.js';
import { AutomationActivityView } from './AutomationActivityView.js';
import { CalendarFollowups } from './CalendarFollowups.js';
import { ChannelBadge } from './ChannelBadge.js';
import { ContactHygieneHub, hygieneBadgeCount } from './ContactHygieneHub.js';
import { initialsOf, ProfilePhoto } from './ProfilePhoto.js';
import { ConnectView } from './queue/ConnectView.js';
import { InvitationsView } from './queue/InvitationsView.js';
import { JobChangesView } from './queue/JobChangesView.js';
import { KeepInTouchView } from './queue/KeepInTouchView.js';
import { LinkedinEngagementView } from './queue/LinkedinEngagementView.js';
import { StatusBadge } from './StatusBadge.js';
import { StatusLegend } from './StatusLegend.js';
import { AlertDock } from './shell/AlertDock.js';
import { AppTopbar } from './shell/AppTopbar.js';
import { type Command, CommandPalette } from './shell/CommandPalette.js';
import { HealthDrawer } from './shell/HealthDrawer.js';
import { MobileTabBar } from './shell/MobileTabBar.js';
import { EmptyState } from './states/EmptyState.js';
import { ErrorState } from './states/ErrorState.js';
import { LoadingState } from './states/LoadingState.js';
import { ThreadDetail } from './ThreadDetail.js';
import { TodayMeetings } from './TodayMeetings.js';
import { type TodayGoal, TodayView } from './TodayView.js';
import { SnoozePopover } from './triage/SnoozePopover.js';
import { TriageActions, TriageProvider } from './triage/Triage.js';
import { UnifiedAddressBook } from './UnifiedAddressBook.js';
import { Dialog } from './ui/Dialog.js';
import { VirtualList } from './ui/VirtualList.js';

type Mode = 'actionable' | 'all';
type ChannelFilter = null | 'linkedin' | 'telegram' | 'whatsapp' | 'email';

// R14b (#9) — chunked Clear-no-reply job. CLEAR_CHUNK rows per request; the client
// loops until the server reports `remaining === 0`, driving the determinate bar.
const CLEAR_CHUNK = 100;
export type ClearJob =
  | { phase: 'idle' }
  | { phase: 'running'; cleared: number; total: number }
  | { phase: 'done'; cleared: number }
  | { phase: 'error'; message: string };
// QA — `UnifiedView` was a hand-maintained 12-member union duplicating `NavView`
// (the destinations.ts single source of truth). Alias it so the two can never drift.
export type UnifiedView = NavView;

const PAGE_LIMIT = 40;

const CHANNEL_OPTIONS: ReadonlyArray<{ value: ChannelFilter; label: string }> = [
  { value: null, label: 'All' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
];

const REASON_LABEL: Record<FollowupReason, string> = {
  i_owe_reply: 'I owe reply',
  waiting_on_them: 'Waiting on them',
  deferred: 'Deferred',
  stale_actionable: 'Stale',
};

const REASON_ORDER: FollowupReason[] = [
  'i_owe_reply',
  'stale_actionable',
  'waiting_on_them',
  'deferred',
];

const ALL_REASONS: FollowupIncludeReason[] = [
  'i_owe_reply',
  'stale_actionable',
  'waiting_on_them',
  'deferred',
];

const STALE_DAYS_OPTIONS = [3, 7, 14, 30] as const;

// Wave-1 — persist the inbox/follow-up filters in the URL (like ?q and ?thread)
// so they survive navigation + reload. Defaults are stored as an ABSENT param so
// a clean URL = the default view; only non-default filters appear.
const CHANNEL_VALUES: ReadonlyArray<Exclude<ChannelFilter, null>> = [
  'linkedin',
  'telegram',
  'whatsapp',
  'email',
];
function parseChannelParam(v: string | null): ChannelFilter {
  return v !== null && (CHANNEL_VALUES as readonly string[]).includes(v)
    ? (v as ChannelFilter)
    : null;
}
function parseStaleParam(v: string | null): number {
  const n = Number(v);
  return (STALE_DAYS_OPTIONS as readonly number[]).includes(n) ? n : 7;
}
/** `null`/absent → all reasons (default); `none` → empty; else the csv subset. */
function parseReasonsParam(v: string | null): Set<FollowupReason> {
  if (v === 'none') return new Set<FollowupReason>();
  if (!v) return new Set(REASON_ORDER);
  const toks = v
    .split(',')
    .filter((t): t is FollowupReason => (REASON_ORDER as string[]).includes(t));
  return toks.length > 0 ? new Set(toks) : new Set(REASON_ORDER);
}
function serializeReasonsParam(active: Set<FollowupReason>): string | null {
  if (active.size === REASON_ORDER.length) return null; // all = default → clean URL
  if (active.size === 0) return 'none';
  return REASON_ORDER.filter((r) => active.has(r)).join(',');
}

interface UnifiedDashboardProps {
  currentView: UnifiedView;
  selectedThreadId: string | null;
  selectedContactId: string | null;
  onSelectThread: (id: string | null) => void;
  onSelectContactId: (id: string | null) => void;
  onOpenThread: (id: string) => void;
  onTriaged: (currentThreadId: string, kind: 'defer' | 'mark_no_response_needed' | 'sent') => void;
  onChangeView: (next: NavView) => void;
  onSelectContact: (id: string) => void;
  onOpenShortcuts: () => void;
}

/**
 * batch-105 — play a chime when a new message arrives, while sound is enabled.
 * Polls the GLOBAL summary (no channel filter) so it works on any tab, diffs the
 * newest-inbound clocks across polls, and plays the actionable vs other tone.
 * Shares the ['threads','summary','unified',undefined] cache key with the inbox
 * column, so it adds no extra fetch when the inbox is open.
 */
function useNewMessageChime() {
  const enabled = useSoundEnabled();
  const summary = useQuery({
    queryKey: ['threads', 'summary', 'unified', undefined],
    queryFn: ({ signal }) => getThreadsSummary({ signal }),
    refetchInterval: LIVE_POLL_MS,
  });
  const prevRef = useRef<InboundClocks | null>(null);
  const data = summary.data;
  useEffect(() => {
    if (!data) return;
    const next: InboundClocks = {
      inboundAt: toMs(data.latest_inbound_at),
      actionableAt: toMs(data.latest_actionable_inbound_at),
    };
    const prev = prevRef.current;
    prevRef.current = next; // keep the baseline current even while muted
    if (!prev || !enabled) return; // first load (baseline) or muted → no chime
    const kind = decideChime(prev, next);
    if (kind) playChime(kind);
  }, [data, enabled]);
}

export function UnifiedDashboard({
  currentView,
  selectedThreadId,
  selectedContactId,
  onSelectThread,
  onSelectContactId,
  onOpenThread,
  onTriaged,
  onChangeView,
  onSelectContact,
  onOpenShortcuts,
}: UnifiedDashboardProps) {
  useNewMessageChime();
  // R8 — global search lives in the URL (`?q`, replace-mode so keystrokes don't
  // spam history). Owned here so the topbar input and the inbox consumer share
  // ONE source (independent useUrlParam instances don't sync on a programmatic set).
  // The document.title is owned by App.tsx.
  const [qParam, setQ] = useUrlParam('q', { replace: true });
  const query = qParam ?? '';

  // R9 — the health drawer open state (opened by the topbar status pill or by an
  // alert-dock action, optionally scrolled to a section).
  const [drawer, setDrawer] = useState<{ open: boolean; section?: DrawerSection }>({ open: false });
  const openDrawer = (section?: DrawerSection) => setDrawer({ open: true, section });

  // R10 — the outreach hubs, lifted out of the inbox column to the rail so they
  // open from any view. Configs gate armed/locked; counts come from work-summary
  // (the R4 count-truth source); the modals are still the existing ones (ported
  // to routed views in EB4).
  const workSummary = useWorkSummary();
  // batch-341 — the "Contact hygiene" destination badge is count-true = the sum of
  // its three tabs (Duplicates = identity_matches from work-summary, Junk +
  // Attribution = hygiene pending). Poll on the same 30s cadence as work-summary.
  const hygieneSummary = useQuery({
    queryKey: ['contact-hygiene-summary'],
    queryFn: ({ signal }) => getHygieneSummary({ signal }),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  const engageCfg = useQuery({
    queryKey: ['engage-config'],
    queryFn: ({ signal }) => getEngageConfig({ signal }),
    staleTime: 60_000,
  });
  const jobChangeCfg = useQuery({
    queryKey: ['job-change-config'],
    queryFn: ({ signal }) => getJobChangeConfig({ signal }),
    staleTime: 60_000,
  });
  const invitationsCfg = useQuery({
    queryKey: ['invitations-config'],
    queryFn: ({ signal }) => getInvitationsConfig({ signal }),
    staleTime: 60_000,
  });
  const keepInTouchCfg = useQuery({
    queryKey: ['keep-in-touch-config'],
    queryFn: ({ signal }) => getKeepInTouchConfig({ signal }),
    staleTime: 60_000,
  });
  const connectCfg = useQuery({
    queryKey: ['connect-config'],
    queryFn: ({ signal }) => getConnectConfig({ signal }),
    staleTime: 60_000,
  });

  // A1 — Triage rail count badges. Inbox = actionable "needs reply" from the global
  // summary: the SAME 4-element key useNewMessageChime already runs unconditionally,
  // so React Query dedupes → NO extra fetch. Follow-ups = open items: we reuse the
  // EXACT key the follow-ups VIEW uses at its defaults (all reasons, 7-day) so the
  // two coalesce WHEN that view is open at defaults; otherwise this is a small extra
  // always-on poll (one bounded listFollowups, slowed to 60s since it's a glanceable
  // badge, not a live list).
  const inboxSummary = useQuery({
    queryKey: ['threads', 'summary', 'unified', undefined],
    queryFn: ({ signal }) => getThreadsSummary({ signal }),
    refetchInterval: LIVE_POLL_MS,
  });
  const followupsRail = useQuery({
    queryKey: ['followups', 'unified', 7, [...ALL_REASONS].sort().join(',')],
    queryFn: ({ signal }) =>
      listFollowups({
        stale_days: 7,
        include: [...ALL_REASONS] as FollowupIncludeReason[],
        signal,
      }),
    staleTime: 10_000,
    refetchInterval: 60_000,
  });
  const inboxCount = inboxSummary.data?.actionable;
  // QA H2 — the TRUE count (server-side, uncapped), not the capped items.length.
  const followupsCount = followupsRail.data?.total;

  // batch-341 — the "Contact hygiene" address-book badge = the sum of the three
  // hub tabs (count-truth, via the pure helper). The Duplicates tab itself still
  // uses the bare identity_matches count.
  const hygieneBadgeCountValue = hygieneBadgeCount(
    workSummary.data?.identity_matches,
    hygieneSummary.data,
  );

  // R10 review — three states, NOT two. While a config is still loading we must
  // NOT paint a lock (it flickers locked→armed); and a locked item must NOT claim
  // "turn it on in Setup" — these are operator/server env flags with no in-app
  // toggle, so the honest explainer just names the state (mirrors the modals'
  // own "switched off in your operator configuration" copy) and the item is a
  // non-navigating tooltip, never a dead-end route.
  const hubStatus = (
    cfg: { isError: boolean; isSuccess: boolean },
    armedFlag: boolean,
  ): HubStatus => {
    if (cfg.isError) return 'locked';
    if (cfg.isSuccess) return armedFlag ? 'armed' : 'locked';
    return 'loading';
  };

  const hubs: HubBundle = {
    engage: {
      status: hubStatus(engageCfg, Boolean(engageCfg.data?.engage_enabled)),
      count: workSummary.data?.engagement,
      open: () => onChangeView('unified-engagement'), // R19 — routed view
      lockedExplainer: 'LinkedIn engagement is off in your operator configuration.',
    },
    jobChanges: {
      status: hubStatus(
        jobChangeCfg,
        Boolean(jobChangeCfg.data?.detect_enabled || jobChangeCfg.data?.drafts_enabled),
      ),
      count: workSummary.data?.job_changes,
      // R17 — Job changes is now a ROUTED view (not a modal); the rail/⌘K "open"
      // navigates to it. Locked/loading handling stays in RailHubItem.
      open: () => onChangeView('unified-job-changes'),
      lockedExplainer: 'Job-change detection is off in your operator configuration.',
    },
    invitations: {
      // Read-only review surface — available whenever the config loads.
      status: hubStatus(invitationsCfg, invitationsCfg.isSuccess),
      count: workSummary.data?.invitations,
      open: () => onChangeView('unified-connection-requests'), // R20 — routed view
      lockedExplainer: 'Connection requests aren’t available right now.',
    },
    keepInTouch: {
      status: hubStatus(keepInTouchCfg, Boolean(keepInTouchCfg.data?.enabled)),
      count: workSummary.data?.keep_in_touch,
      open: () => onChangeView('unified-keep-in-touch'), // R18 — routed view
      lockedExplainer: 'Keep in touch is switched off in your operator configuration.',
    },
    connect: {
      // batch-301 — "People you met": propose LinkedIn connects from meeting attendees.
      status: hubStatus(connectCfg, Boolean(connectCfg.data?.enabled)),
      count: workSummary.data?.connect, // QA M4 — was hardcoded undefined → always "—"
      open: () => onChangeView('unified-connect'),
      lockedExplainer: 'Connect-with-people-you-met is off in your operator configuration.',
    },
  };

  // R13 — the ⌘K command palette (Cmd/Ctrl+K). Registry-fed: a jump to any
  // reachable destination — the view destinations + the ARMED outreach hubs
  // (locked/loading hubs aren't reachable, so they're omitted, like a rail you
  // can't click). The modal-opening hubs are reachable here because the palette
  // lives where they do (unlike the App-level g-keys, which cover views only).
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k')) return;
      // R13 review — don't steal ⌘K mid-typing in a text field. When the palette
      // is already open we DO allow it, so ⌘K closes it even from its own input.
      if (!paletteOpen && isEditableTarget(e.target)) return;
      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen]);

  const commands = buildCommands(hubs, onChangeView);

  // B2 — the "Today" home goal cards, ordered by the operator's 4 goals. Counts come
  // from the SAME sources as the rail badges (inbox summary, follow-ups, work-summary)
  // — no new fetch, no new API. Outreach goals appear only when their hub is armed.
  const dt = workSummary.data?.done_today;
  const todayGoals: TodayGoal[] = [
    {
      key: 'inbox',
      label: DESTINATIONS.inbox.label,
      icon: <InboxGlyph />,
      count: inboxCount,
      cta: 'Triage',
      view: 'unified',
    },
    {
      key: 'followups',
      label: DESTINATIONS.followups.label,
      icon: <ClockGlyph />,
      count: followupsCount,
      cta: 'Review',
      view: 'unified-followups',
    },
  ];
  if (hubs.keepInTouch.status === 'armed')
    todayGoals.push({
      key: 'keep-in-touch',
      label: DESTINATIONS.keepInTouch.label,
      icon: <HeartGlyph />,
      count: hubs.keepInTouch.count,
      doneToday: dt?.keep_in_touch,
      cta: 'Review',
      view: 'unified-keep-in-touch',
    });
  if (hubs.engage.status === 'armed')
    todayGoals.push({
      key: 'engagement',
      label: DESTINATIONS.engagement.label,
      icon: <EngageGlyph />,
      count: hubs.engage.count,
      doneToday: dt?.engagement,
      cta: 'Engage',
      view: 'unified-engagement',
    });
  if (hubs.invitations.status === 'armed')
    todayGoals.push({
      key: 'connection-requests',
      label: DESTINATIONS.connectionRequests.label,
      icon: <ConnectGlyph />,
      count: hubs.invitations.count,
      doneToday: dt?.invitations,
      cta: 'Review',
      view: 'unified-connection-requests',
    });
  if (hubs.jobChanges.status === 'armed')
    todayGoals.push({
      key: 'job-changes',
      label: DESTINATIONS.jobChanges.label,
      icon: <BriefcaseGlyph />,
      count: hubs.jobChanges.count,
      doneToday: dt?.job_changes,
      cta: 'Review',
      view: 'unified-job-changes',
    });
  if (hubs.connect.status === 'armed')
    todayGoals.push({
      key: 'connect',
      label: DESTINATIONS.connect.label,
      icon: <ConnectGlyph />,
      count: hubs.connect.count,
      cta: 'Review',
      view: 'unified-connect',
    });

  return (
    <div className="unified-dashboard" data-testid="unified-dashboard">
      <AppTopbar
        currentView={currentView}
        query={query}
        onQueryChange={setQ}
        onOpenShortcuts={onOpenShortcuts}
        onOpenDrawer={openDrawer}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <AlertDock onOpenDrawer={openDrawer} />
      <HealthDrawer
        open={drawer.open}
        section={drawer.section}
        onClose={() => setDrawer({ open: false })}
      />
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={commands}
      />
      <UnifiedNav
        currentView={currentView}
        onChangeView={onChangeView}
        hubs={hubs}
        inboxCount={inboxCount}
        followupsCount={followupsCount}
      />
      {/* R24 — narrow-viewport primary nav (CSS shows it only ≤860; the rail hides). */}
      <MobileTabBar
        currentView={currentView}
        onChangeView={onChangeView}
        onOpenMore={() => setPaletteOpen(true)}
      />
      <div className="unified-main">
        {currentView === 'unified-today' ? (
          <TodayView
            goals={todayGoals}
            onChangeView={onChangeView}
            beforeGrid={
              <>
                <TodayMeetings onChangeView={onChangeView} onSelectThread={onSelectThread} />
                <CalendarFollowups />
              </>
            }
          />
        ) : null}
        {currentView === 'unified' ? (
          <UnifiedInboxWorkspace
            selectedThreadId={selectedThreadId}
            onSelectThread={onSelectThread}
            onTriaged={onTriaged}
            onSelectContact={onSelectContact}
            query={query}
          />
        ) : null}
        {currentView === 'unified-followups' ? (
          <UnifiedFollowupsWorkspace
            selectedThreadId={selectedThreadId}
            onSelectThread={onSelectThread}
            onTriaged={onTriaged}
            onSelectContact={onSelectContact}
          />
        ) : null}
        {currentView === 'unified-job-changes' ? <JobChangesView /> : null}
        {currentView === 'unified-keep-in-touch' ? <KeepInTouchView /> : null}
        {currentView === 'unified-connect' ? <ConnectView /> : null}
        {currentView === 'unified-engagement' ? <LinkedinEngagementView /> : null}
        {currentView === 'unified-connection-requests' ? <InvitationsView /> : null}
        {currentView === 'unified-contacts' ? (
          <UnifiedContactsWorkspace
            selectedContactId={selectedContactId}
            onSelectContactId={onSelectContactId}
            onOpenThread={onOpenThread}
            onChangeView={onChangeView}
            identityMatchesCount={hygieneBadgeCountValue}
          />
        ) : null}
        {currentView === 'unified-candidates' ? (
          <UnifiedCandidatesWorkspace
            onSelectContact={onSelectContact}
            onBack={() => onChangeView('unified-contacts')}
            duplicatesCount={workSummary.data?.identity_matches}
          />
        ) : null}
        {currentView === 'unified-automation' ? <AutomationActivityView /> : null}
        {currentView === 'unified-settings' ? <UnifiedSettingsWorkspace /> : null}
      </div>
    </div>
  );
}

/** R10 — an outreach hub's rail state: armed (open the modal + show the count),
 * locked (off in operator config — a tooltip-only, non-navigating item), or
 * loading (config not yet resolved — neutral, no lock, no badge). The modals are
 * ported to routed views in EB4; until then the rail opens them. */
export type HubStatus = 'armed' | 'locked' | 'loading';

export interface HubState {
  status: HubStatus;
  count: number | undefined;
  open: () => void;
  lockedExplainer: string;
}

export interface HubBundle {
  engage: HubState;
  jobChanges: HubState;
  invitations: HubState;
  keepInTouch: HubState;
  connect: HubState;
}

/**
 * R13 — the ⌘K palette commands, registry-fed. The 5 VIEW destinations + Classic
 * (always reachable) + each outreach hub ONLY when it's armed (a locked/loading
 * hub isn't reachable, so it's omitted — no dead "Open X"). Exported so the
 * status-gated omission is unit-tested.
 */
export function buildCommands(hubs: HubBundle, onChangeView: (next: NavView) => void): Command[] {
  const goView = (next: NavView) => () => onChangeView(next);
  const hub = (h: HubState, id: string, label: string): Command[] =>
    h.status === 'armed' ? [{ id, label: `Open ${label}`, run: h.open }] : [];

  return [
    { id: 'go-today', label: `Go to ${DESTINATIONS.today.label}`, run: goView('unified-today') },
    { id: 'go-inbox', label: `Go to ${DESTINATIONS.inbox.label}`, run: goView('unified') },
    {
      id: 'go-followups',
      label: `Go to ${DESTINATIONS.followups.label}`,
      run: goView('unified-followups'),
    },
    {
      id: 'go-contacts',
      label: `Go to ${DESTINATIONS.contacts.label}`,
      run: goView('unified-contacts'),
    },
    {
      id: 'go-identity-matches',
      label: `Go to ${DESTINATIONS.identityMatches.label}`,
      run: goView('unified-candidates'),
    },
    {
      id: 'go-automation',
      label: `Go to ${DESTINATIONS.automation.label}`,
      run: goView('unified-automation'),
    },
    { id: 'go-setup', label: `Go to ${DESTINATIONS.setup.label}`, run: goView('unified-settings') },
    ...hub(hubs.engage, 'open-engagement', DESTINATIONS.engagement.label),
    ...hub(hubs.jobChanges, 'open-job-changes', DESTINATIONS.jobChanges.label),
    ...hub(hubs.invitations, 'open-connection-requests', DESTINATIONS.connectionRequests.label),
    ...hub(hubs.keepInTouch, 'open-keep-in-touch', DESTINATIONS.keepInTouch.label),
    ...hub(hubs.connect, 'open-connect', DESTINATIONS.connect.label),
  ];
}

/**
 * R10 — the nav rail: TRIAGE / OUTREACH / PEOPLE groups + a footer Setup. The
 * complete, truthful map of the work. Badges come from `useWorkSummary` (the R4
 * count-truth source) exclusively. Outreach items the operator hasn't enabled
 * show a lock + explainer instead of a dead control. (Classic retired in R26.)
 */
export function UnifiedNav({
  currentView,
  onChangeView,
  hubs,
  inboxCount,
  followupsCount,
}: {
  currentView: UnifiedView;
  onChangeView: (next: NavView) => void;
  hubs: HubBundle;
  /** A1 — Triage-group count badges (optional; absent in unit tests that render the
   *  rail in isolation). Inbox = actionable "needs reply"; Follow-ups = open items. */
  inboxCount?: number;
  followupsCount?: number;
}) {
  return (
    <nav className="unified-nav" aria-label="Primary navigation">
      <RailGroup label="Triage">
        <RailViewItem
          view="unified-today"
          label={DESTINATIONS.today.label}
          icon={<TodayGlyph />}
          currentView={currentView}
          onChangeView={onChangeView}
        />
        <RailViewItem
          view="unified"
          label={DESTINATIONS.inbox.label}
          icon={<InboxGlyph />}
          currentView={currentView}
          onChangeView={onChangeView}
          count={inboxCount}
        />
        <RailViewItem
          view="unified-followups"
          label={DESTINATIONS.followups.label}
          icon={<ClockGlyph />}
          currentView={currentView}
          onChangeView={onChangeView}
          count={followupsCount}
        />
      </RailGroup>

      <RailGroup label="Outreach">
        <RailHubItem
          label={DESTINATIONS.engagement.label}
          icon={<EngageGlyph />}
          hub={hubs.engage}
          testId="rail-engagement"
          active={currentView === 'unified-engagement'}
        />
        <RailHubItem
          label={DESTINATIONS.jobChanges.label}
          icon={<BriefcaseGlyph />}
          hub={hubs.jobChanges}
          testId="rail-job-changes"
          active={currentView === 'unified-job-changes'}
        />
        <RailHubItem
          label={DESTINATIONS.connectionRequests.label}
          icon={<ConnectGlyph />}
          hub={hubs.invitations}
          testId="rail-invitations"
          active={currentView === 'unified-connection-requests'}
        />
        <RailHubItem
          label={DESTINATIONS.keepInTouch.label}
          icon={<HeartGlyph />}
          hub={hubs.keepInTouch}
          testId="rail-keep-in-touch"
          active={currentView === 'unified-keep-in-touch'}
        />
        <RailHubItem
          label={DESTINATIONS.connect.label}
          icon={<ConnectGlyph />}
          hub={hubs.connect}
          testId="rail-connect"
          active={currentView === 'unified-connect'}
        />
      </RailGroup>

      <RailGroup label="People">
        <RailViewItem
          view="unified-contacts"
          label={DESTINATIONS.contacts.label}
          icon={<PeopleGlyph />}
          currentView={currentView}
          onChangeView={onChangeView}
        />
        {/* R22 — Identity matches absorbed into Contacts (a pinned row in the address
            book); the standalone rail item is gone. The view stays reachable via ⌘K /
            the pinned row + ?view=unified-candidates. */}
      </RailGroup>

      <div className="rail-foot">
        <RailViewItem
          view="unified-automation"
          label={DESTINATIONS.automation.label}
          icon={<AutomationGlyph />}
          currentView={currentView}
          onChangeView={onChangeView}
        />
        <RailViewItem
          view="unified-settings"
          label={DESTINATIONS.setup.label}
          icon={<GearGlyph />}
          currentView={currentView}
          onChangeView={onChangeView}
        />
      </div>
    </nav>
  );
}

/** A labelled rail group. The rail is a <nav> landmark; full programmatic
 * grouping (list/role=group semantics) is part of the R24 a11y pass. */
function RailGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rail-group">
      <div className="rail-group-label">{label}</div>
      {children}
    </div>
  );
}

function RailViewItem({
  view,
  label,
  icon,
  currentView,
  onChangeView,
  count,
}: {
  view: NavView;
  label: string;
  icon: ReactNode;
  currentView: UnifiedView;
  onChangeView: (next: NavView) => void;
  /** A1 — optional pending-work count badge (Inbox / Follow-ups), so the #1 goal
   *  (zero-inbox) is glanceable from the rail like the outreach hubs already are. */
  count?: number;
}) {
  const active = view === currentView;
  return (
    <button
      type="button"
      data-testid={`unified-nav-${view}`}
      aria-current={active ? 'page' : undefined}
      aria-label={countLabel(label, count) ?? label}
      className={active ? 'rail-item active' : 'rail-item'}
      onClick={() => onChangeView(view)}
      title={label}
    >
      <span aria-hidden="true" className="rail-icon">
        {icon}
      </span>
      <span className="rail-label">{label}</span>
      <RailBadge count={count} />
    </button>
  );
}

function RailHubItem({
  label,
  icon,
  hub,
  testId,
  active = false,
}: {
  label: string;
  icon: ReactNode;
  hub: HubState;
  testId: string;
  /** R17 — for a hub that now NAVIGATES to a routed view, highlight when it's the
   * current view (mirrors RailViewItem's active state). */
  active?: boolean;
}) {
  if (hub.status === 'armed') {
    return (
      <button
        type="button"
        data-testid={testId}
        className={active ? 'rail-item active' : 'rail-item'}
        aria-current={active ? 'page' : undefined}
        aria-label={countLabel(label, hub.count)}
        onClick={hub.open}
        title={label}
      >
        <span aria-hidden="true" className="rail-icon">
          {icon}
        </span>
        <span className="rail-label">{label}</span>
        <RailBadge count={hub.count} />
      </button>
    );
  }
  if (hub.status === 'loading') {
    // Config not resolved yet — neutral, non-actionable, no lock/badge (so the
    // item never flashes a scary "locked" before the real state lands).
    return (
      <span data-testid={testId} className="rail-item loading" aria-hidden="true">
        <span className="rail-icon">{icon}</span>
        <span className="rail-label">{label}</span>
      </span>
    );
  }
  // Locked — off in operator config. A tooltip-only, NON-navigating control (no
  // Setup route: Setup can't toggle these env flags, so routing there would be a
  // dead-end / decoy). The state + reason go into the accessible name.
  return (
    <button
      type="button"
      data-testid={testId}
      className="rail-item locked"
      aria-disabled="true"
      aria-label={`${label} — locked. ${hub.lockedExplainer}`}
      title={hub.lockedExplainer}
    >
      <span aria-hidden="true" className="rail-icon">
        {icon}
      </span>
      <span className="rail-label">{label}</span>
      <span className="rail-lock" aria-hidden="true">
        🔒
      </span>
    </button>
  );
}

/** Accessible name that folds a pending count into the item label. */
function countLabel(label: string, count: number | undefined): string | undefined {
  return count && count > 0 ? `${label}, ${count} pending` : undefined;
}

function RailBadge({ count }: { count: number | undefined }) {
  if (!count || count <= 0) return null;
  // Visual only — the count is announced via the item's aria-label (countLabel).
  return (
    <span className="rail-count" aria-hidden="true">
      {count > 999 ? '999+' : count}
    </span>
  );
}

function UnifiedInboxWorkspace({
  selectedThreadId,
  onSelectThread,
  onTriaged,
  onSelectContact,
  query,
}: {
  selectedThreadId: string | null;
  onSelectThread: (id: string | null) => void;
  onTriaged: (currentThreadId: string, kind: 'defer' | 'mark_no_response_needed' | 'sent') => void;
  onSelectContact: (id: string) => void;
  query: string;
}) {
  return (
    <TriageProvider onTriaged={onTriaged} onReselect={onSelectThread}>
      <div className="unified-workspace">
        <UnifiedInboxColumn
          selectedId={selectedThreadId}
          onSelect={onSelectThread}
          searchTerm={query}
        />
        <section className="unified-conversation" aria-label="Conversation">
          {selectedThreadId ? (
            <div className="unified-canvas-triage" data-testid="canvas-triage">
              <TriageActions threadId={selectedThreadId} variant="canvas" />
            </div>
          ) : null}
          <ThreadDetail threadId={selectedThreadId} onTriaged={onTriaged} />
        </section>
        <UnifiedRightRail threadId={selectedThreadId} onSelectContact={onSelectContact} />
      </div>
    </TriageProvider>
  );
}

function UnifiedFollowupsWorkspace({
  selectedThreadId,
  onSelectThread,
  onTriaged,
  onSelectContact,
}: {
  selectedThreadId: string | null;
  onSelectThread: (id: string | null) => void;
  onTriaged: (currentThreadId: string, kind: 'defer' | 'mark_no_response_needed' | 'sent') => void;
  onSelectContact: (id: string) => void;
}) {
  return (
    <TriageProvider onTriaged={onTriaged} onReselect={onSelectThread}>
      <div className="unified-workspace">
        <UnifiedFollowupsColumn
          selectedId={selectedThreadId}
          onSelect={(id) => onSelectThread(id)}
        />
        <section className="unified-conversation" aria-label="Conversation">
          {selectedThreadId ? (
            <div className="unified-canvas-triage" data-testid="canvas-triage">
              <TriageActions threadId={selectedThreadId} variant="canvas" />
            </div>
          ) : null}
          <ThreadDetail threadId={selectedThreadId} onTriaged={onTriaged} />
        </section>
        <UnifiedRightRail threadId={selectedThreadId} onSelectContact={onSelectContact} />
      </div>
    </TriageProvider>
  );
}

function UnifiedContactsWorkspace({
  selectedContactId,
  onSelectContactId,
  onOpenThread,
  onChangeView,
  identityMatchesCount,
}: {
  selectedContactId: string | null;
  onSelectContactId: (id: string | null) => void;
  onOpenThread: (id: string) => void;
  onChangeView: (next: NavView) => void;
  identityMatchesCount: number | undefined;
}) {
  return (
    <UnifiedAddressBook
      selectedContactId={selectedContactId}
      onSelectContactId={onSelectContactId}
      onOpenThread={onOpenThread}
      identityMatchesCount={identityMatchesCount}
      onShowIdentityMatches={() => onChangeView('unified-candidates')}
      onOpenEngagement={() => onChangeView('unified-engagement')}
    />
  );
}

function UnifiedCandidatesWorkspace({
  onSelectContact,
  onBack,
  duplicatesCount,
}: {
  onSelectContact: (id: string) => void;
  onBack: () => void;
  /** Pending duplicate candidates — the Duplicates-tab badge (count-truth). */
  duplicatesCount: number | undefined;
}) {
  // batch-341 — the admin review is now the tabbed Contact-hygiene hub: the
  // EXISTING candidates panel becomes the Duplicates tab, alongside Junk +
  // Attribution + the auto-archived reference list.
  return (
    <div className="unified-workspace unified-workspace-admin">
      <UnifiedAdminMenu onBack={onBack} />
      <section className="unified-conversation unified-panel-host" aria-label="Contact hygiene">
        <ContactHygieneHub
          onSelectContact={onSelectContact}
          duplicatesCount={duplicatesCount}
          duplicatesPanel={<UnifiedCandidatesPanel onSelectContact={onSelectContact} />}
        />
      </section>
      <UnifiedAdminRail />
    </div>
  );
}

// R23 — Setup is now a sectioned surface (deep-linked via ?section=). Each section
// is a focused page; the menu is real navigation, not a static blurb.
const SETUP_SECTIONS = [
  { id: 'posture', label: 'Sending & automation', kicker: 'Posture' },
  { id: 'channels', label: 'Channels', kicker: 'Connectors' },
  { id: 'calendar', label: 'Calendar import', kicker: 'Integration' },
  { id: 'learning', label: 'AI & learning', kicker: 'Voice' },
  { id: 'security', label: 'Security', kicker: 'Passkeys' },
  { id: 'notifications', label: 'Notifications', kicker: 'Sound' },
  { id: 'archived', label: 'Archived contacts', kicker: 'People' },
  { id: 'about', label: 'About', kicker: 'System' },
] as const;
type SetupSection = (typeof SETUP_SECTIONS)[number]['id'];
function isSetupSection(v: string | null): v is SetupSection {
  return SETUP_SECTIONS.some((s) => s.id === v);
}

export function UnifiedSettingsWorkspace() {
  const [sectionParam, setSection] = useUrlParam('section', { replace: true });
  const section: SetupSection = isSetupSection(sectionParam) ? sectionParam : 'posture';
  return (
    <div className="unified-workspace unified-workspace-settings">
      <UnifiedSetupMenu
        section={section}
        onSection={(s) => setSection(s === 'posture' ? null : s)}
      />
      <section className="unified-conversation unified-panel-host" aria-label="Workspace setup">
        <UnifiedSettingsPanel section={section} />
      </section>
      <UnifiedStatusRail />
    </div>
  );
}

function UnifiedInboxColumn({
  selectedId,
  onSelect,
  searchTerm,
}: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  searchTerm: string;
}) {
  // Wave-1 — mode + channel persist in the URL (replace mode: a filter change
  // shouldn't add a Back-button stop). Default 'actionable'/all-channels is stored
  // as an absent param, so a clean URL restores the default.
  const [modeParam, setModeParam] = useUrlParam('inbox', { replace: true });
  const mode: Mode = modeParam === 'all' ? 'all' : 'actionable';
  const setMode = useCallback(
    (m: Mode) => setModeParam(m === 'all' ? 'all' : null),
    [setModeParam],
  );
  const [chanParam, setChanParam] = useUrlParam('chan', { replace: true });
  const channel: ChannelFilter = parseChannelParam(chanParam);
  const setChannel = useCallback((c: ChannelFilter) => setChanParam(c), [setChanParam]);
  // R8 — the search box was lifted to the global topbar (`?q`); this column now
  // consumes that shared query rather than owning its own input.
  // R10 — the outreach hubs (LI engagement / job changes / connection requests /
  // keep in touch) + their modals moved out of here to the nav rail.
  // R14 — debounce 250ms so the query key / count refetch only fire after a pause.
  const q = useDebouncedValue(searchTerm.trim(), 250);

  // batch-144 — inbox-zero: clear every thread the AI already judged needs no
  // response (retroactive apply of the auto-mark-read rule).
  // R14b (#9) — now a CHUNKED job with a determinate "N of M" bar: loop the chunked
  // endpoint until `remaining === 0`, showing real progress on a big backlog
  // (the old one-shot UPDATE gave none). Lives in the ⋯ menu; the bar is visible.
  const inboxQc = useQueryClient();
  const [clearJob, setClearJob] = useState<ClearJob>({ phase: 'idle' });
  const clearRunningRef = useRef(false);
  const overflowRef = useRef<HTMLDetailsElement>(null);
  // R14b review (#12) — don't setState / keep POSTing after the column unmounts.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // R14b review (#6) — native <details> doesn't close on Escape / outside-click;
  // add both so the ⋯ menu behaves like a menu.
  useEffect(() => {
    const el = overflowRef.current;
    if (!el) return;
    const closeOutside = (e: PointerEvent) => {
      if (el.open && !el.contains(e.target as Node)) el.open = false;
    };
    const closeEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && el.open) {
        el.open = false;
        el.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, []);

  const runClearNoReply = useCallback(async () => {
    if (clearRunningRef.current) return; // guard double-start
    clearRunningRef.current = true;
    setClearJob({ phase: 'running', cleared: 0, total: 0 });
    let cleared = 0;
    let total = 0;
    try {
      // Bounded loop (defensive ceiling); each call clears ≤ CLEAR_CHUNK.
      for (let i = 0; i < 1000; i++) {
        const res = await clearNoReply(CLEAR_CHUNK);
        if (!mountedRef.current) return; // unmounted mid-loop → stop
        cleared += res.cleared;
        if (i === 0) total = res.cleared + res.remaining;
        setClearJob({ phase: 'running', cleared, total: Math.max(total, cleared) });
        // Done when nothing remains, or a chunk made no progress (guards against
        // an unexpected non-decreasing remaining → never an infinite loop).
        if (res.remaining <= 0 || res.cleared === 0) break;
      }
      void inboxQc.invalidateQueries({ queryKey: ['threads'] });
      if (mountedRef.current) setClearJob({ phase: 'done', cleared });
    } catch (e) {
      if (mountedRef.current) {
        setClearJob({ phase: 'error', message: e instanceof Error ? e.message : 'Failed' });
      }
    } finally {
      clearRunningRef.current = false;
    }
  }, [inboxQc]);

  const query = useInfiniteQuery({
    queryKey: ['threads', 'unified', mode, channel, q],
    queryFn: ({ pageParam, signal }) =>
      listThreads({
        limit: PAGE_LIMIT,
        before: pageParam,
        signal,
        ...(mode === 'actionable' ? { actionable: true } : {}),
        ...(channel ? { channel } : {}),
        ...(q ? { q } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // batch-105 — live inbox: new conversations/messages surface without reload.
    refetchInterval: LIVE_POLL_MS,
    // Inbox-rebuild Phase 0 — keep the current list on screen while a filter/search
    // change (or a poll) fetches, instead of flashing the Loading/empty state.
    placeholderData: keepPreviousData,
  });

  const summary = useQuery({
    // R14 — counts respect q + channel so the toggle number equals the list.
    queryKey: ['threads', 'summary', 'unified', channel, q],
    queryFn: ({ signal }) =>
      getThreadsSummary({ signal, ...(channel ? { channel } : {}), ...(q ? { q } : {}) }),
    // Phase 0 — 30s (match LIVE_POLL_MS); the 5s was below the poll so it only
    // bought extra focus-refetches. keepPreviousData keeps the counts stable.
    staleTime: 30_000,
    refetchInterval: LIVE_POLL_MS,
    placeholderData: keepPreviousData,
  });

  const threads = useMemo<ThreadListItemDto[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  useEffect(() => {
    if (selectedId === null && threads.length > 0) onSelect(threads[0]?.id ?? null);
  }, [onSelect, selectedId, threads]);

  return (
    <section className="unified-inbox" aria-label="Unified inbox list">
      <div className="unified-inbox-header">
        <div className="unified-mode-toggle" role="tablist" aria-label="Inbox mode">
          <ModeButton
            mode={mode}
            value="actionable"
            label="Needs reply"
            count={summary.data?.actionable}
            onSelect={setMode}
          />
          <ModeButton
            mode={mode}
            value="all"
            label="All"
            count={summary.data?.total}
            onSelect={setMode}
          />
        </div>

        <StatusLegend />

        {mode === 'actionable' ? (
          <div className="unified-inbox-tidy">
            {clearJob.phase !== 'idle' ? (
              <ClearNoReplyJob job={clearJob} onDismiss={() => setClearJob({ phase: 'idle' })} />
            ) : null}
            {/* R14b review (#5) — native <details> disclosure; no ARIA menu roles
                (a <summary> revealing a panel of <button>s is already accessible). */}
            <details className="unified-overflow" ref={overflowRef}>
              <summary
                className="unified-overflow-btn"
                data-testid="inbox-overflow"
                aria-label="Inbox actions"
                title="Inbox actions"
              >
                ⋯
              </summary>
              <div className="unified-overflow-menu">
                <button
                  type="button"
                  className="unified-overflow-item"
                  data-testid="clear-no-reply"
                  disabled={clearJob.phase === 'running'}
                  onClick={(e) => {
                    // R14b review (#7) — return focus to the trigger before the
                    // panel collapses (else focus is dumped to <body>).
                    const d = e.currentTarget.closest('details');
                    d?.removeAttribute('open');
                    d?.querySelector('summary')?.focus();
                    void runClearNoReply();
                  }}
                >
                  <span>✓ Clear no-reply</span>
                  <span className="unified-overflow-hint">
                    Mark read every thread the AI judged needs no response
                  </span>
                </button>
              </div>
            </details>
          </div>
        ) : null}

        <ChannelTabs channel={channel} onChange={setChannel} />
      </div>

      <ThreadListSurface
        query={query}
        threads={threads}
        selectedId={selectedId}
        onSelect={onSelect}
        emptyTitle={mode === 'actionable' ? 'Nothing waiting' : 'No conversations'}
        emptyHint={
          mode === 'actionable'
            ? 'Switch to All to browse the archive.'
            : 'Try a different channel or search term.'
        }
      />
    </section>
  );
}

function UnifiedFollowupsColumn({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  // Wave-1 — stale-threshold + active reason buckets persist in the URL (replace
  // mode). Default (7d / all reasons) is an absent param; `none` = no buckets.
  const [staleParam, setStaleParam] = useUrlParam('stale', { replace: true });
  const staleDays = parseStaleParam(staleParam);
  const setStaleDays = useCallback(
    (d: number) => setStaleParam(d === 7 ? null : String(d)),
    [setStaleParam],
  );
  const [reasonsParam, setReasonsParam] = useUrlParam('reasons', { replace: true });
  const active = useMemo(() => parseReasonsParam(reasonsParam), [reasonsParam]);

  const query = useQuery({
    queryKey: ['followups', 'unified', staleDays, [...active].sort().join(',')],
    queryFn: ({ signal }) =>
      listFollowups({
        stale_days: staleDays,
        include: [...active] as FollowupIncludeReason[],
        signal,
      }),
    staleTime: 10_000,
    enabled: active.size > 0,
    refetchInterval: 30_000,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => {
    const m = new Map<FollowupReason, number>();
    for (const r of REASON_ORDER) m.set(r, 0);
    for (const item of items) m.set(item.followup_reason, (m.get(item.followup_reason) ?? 0) + 1);
    return m;
  }, [items]);

  useEffect(() => {
    if (selectedId === null && items.length > 0) onSelect(items[0]?.id ?? '');
  }, [items, onSelect, selectedId]);

  const toggleReason = (reason: FollowupReason) => {
    const next = new Set(active);
    if (next.has(reason)) next.delete(reason);
    else next.add(reason);
    setReasonsParam(serializeReasonsParam(next));
  };

  return (
    <section className="unified-inbox" aria-label="Follow-ups list">
      <div className="unified-inbox-header">
        <div className="unified-list-title-row">
          <div>
            <div className="unified-side-kicker">Follow-up buckets</div>
            <strong>{items.length} active</strong>
          </div>
          <label className="unified-select-label">
            <span>Stale after</span>
            <select
              value={staleDays}
              onChange={(e) => setStaleDays(Number(e.target.value))}
              data-testid="unified-stale-days-select"
              aria-label="Stale threshold"
            >
              {STALE_DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>{`${d}d`}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="unified-chip-grid" data-testid="unified-reason-filters">
          {REASON_ORDER.map((reason) => (
            <button
              key={reason}
              type="button"
              className={active.has(reason) ? 'unified-filter-chip active' : 'unified-filter-chip'}
              aria-pressed={active.has(reason)}
              data-testid={`unified-reason-filter-${reason}`}
              onClick={() => toggleReason(reason)}
            >
              <span>{REASON_LABEL[reason]}</span>
              <strong>{counts.get(reason) ?? 0}</strong>
            </button>
          ))}
        </div>
      </div>
      <div className="unified-thread-list">
        {active.size === 0 ? (
          <EmptyState title="Pick a bucket" hint="Enable at least one follow-up reason." />
        ) : query.isPending ? (
          <LoadingState label="Loading follow-ups" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            title="No follow-ups"
            hint={`Nothing matched the selected buckets at stale after ${staleDays}d.`}
          />
        ) : (
          <ul>
            {items.map((item) => (
              <UnifiedFollowupRow
                key={item.id}
                item={item}
                selected={item.id === selectedId}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function UnifiedCandidatesPanel({ onSelectContact }: { onSelectContact: (id: string) => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['candidates', 'pending', 'unified'],
    queryFn: ({ signal }) => listCandidates({ status: 'pending', signal }),
    refetchInterval: 60_000,
  });

  // batch-152 — surface confirm/reject failures (were silent).
  const [actionErr, setActionErr] = useState<string | null>(null);
  const confirmMutation = useMutation({
    mutationFn: (id: string) => confirmCandidate(id),
    onSuccess: () => {
      setActionErr(null);
      return queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
    onError: (e) => setActionErr(e instanceof Error ? e.message : 'merge failed'),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: string) => rejectCandidate(id),
    onSuccess: () => {
      setActionErr(null);
      return queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
    onError: (e) => setActionErr(e instanceof Error ? e.message : 'reject failed'),
  });
  const scanMutation = useMutation({
    mutationFn: () => scanForDuplicates(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['candidates'] }),
  });

  if (query.isPending) return <LoadingState label="Loading identity review" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const items = query.data.items;
  const scan = scanMutation.data;

  return (
    <div className="unified-detail-scroll" data-testid="unified-candidates-panel">
      <div className="unified-page-card">
        <div className="unified-card-header">
          <div>
            <div className="unified-side-kicker">Identity review</div>
            <h2>Possible same person</h2>
          </div>
          <div className="unified-card-actions">
            <span className="unified-count-pill">{items.length} pending</span>
            <button
              type="button"
              className="unified-btn"
              data-testid="candidates-scan"
              onClick={() => scanMutation.mutate()}
              disabled={scanMutation.isPending}
            >
              {scanMutation.isPending ? 'Scanning…' : 'Find duplicates'}
            </button>
          </div>
        </div>
        <p>
          Confirming a pair merges the two contacts into one (reversible from the contact card). Use{' '}
          <strong>Find duplicates</strong> to re-scan all contacts for new matches.
        </p>
        {actionErr ? (
          <p className="unified-scan-result" data-testid="candidates-action-error">
            Action failed: {actionErr}
          </p>
        ) : null}
        {scanMutation.isError ? (
          <p className="unified-scan-result" data-testid="candidates-scan-error">
            Scan failed — try again.
          </p>
        ) : scan ? (
          <p className="unified-scan-result" data-testid="candidates-scan-result">
            Scanned {scan.scanned_contacts} contacts · {scan.inserted} new ·{' '}
            {scan.deleted_stale_pending} cleared.
          </p>
        ) : null}
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="No candidates pending"
          hint="Click 'Find duplicates' above to scan all contacts for new possible matches."
        />
      ) : (
        <ul className="unified-card-list">
          {items.map((candidate) => (
            <UnifiedCandidateRow
              key={candidate.id}
              candidate={candidate}
              onSelectContact={onSelectContact}
              onConfirm={() => confirmMutation.mutate(candidate.id)}
              onReject={() => rejectMutation.mutate(candidate.id)}
              pending={
                (confirmMutation.isPending && confirmMutation.variables === candidate.id) ||
                (rejectMutation.isPending && rejectMutation.variables === candidate.id)
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function UnifiedSettingsPanel({ section }: { section: SetupSection }) {
  return (
    <div className="unified-detail-scroll" data-testid="unified-settings-panel">
      {section === 'posture' ? <UnifiedAutomationPosture /> : null}
      {section === 'channels' ? <UnifiedChannelHealthSection /> : null}
      {section === 'calendar' ? <UnifiedCalendarImportSection /> : null}
      {section === 'learning' ? (
        <div className="unified-settings-grid">
          <UnifiedLearningCard />
          <UnifiedStyleGuideCard />
          <UnifiedEngagementInterestsCard />
        </div>
      ) : null}
      {section === 'security' ? <UnifiedSecuritySection /> : null}
      {section === 'notifications' ? <UnifiedNotificationsSection /> : null}
      {section === 'archived' ? <UnifiedArchivedSection /> : null}
      {section === 'about' ? <UnifiedAboutSection /> : null}
    </div>
  );
}

/** R23 — a single automation's live armed/off posture. */
function PostureRow({
  label,
  on,
  detail,
}: {
  label: string;
  on: boolean | undefined;
  detail?: string;
}) {
  return (
    <div
      className="setup-posture-row"
      data-testid={`posture-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="setup-posture-label">{label}</span>
      <span className={`setup-posture-state ${on === undefined ? 'unknown' : on ? 'on' : 'off'}`}>
        {on === undefined ? '—' : on ? 'Armed' : 'Off'}
      </span>
      {detail ? <span className="setup-posture-detail">{detail}</span> : null}
    </div>
  );
}

/**
 * R23 — "Sending & automation" reads the LIVE server config (no hardcoded claims):
 * the send master + per-feature arming, so the operator sees exactly what is armed.
 */
function UnifiedAutomationPosture() {
  const send = useQuery({
    queryKey: ['send-config'],
    queryFn: () => getSendConfig(),
    staleTime: 30_000,
  });
  const engage = useQuery({
    queryKey: ['engage-config'],
    queryFn: ({ signal }) => getEngageConfig({ signal }),
  });
  const jobs = useQuery({
    queryKey: ['job-change-config'],
    queryFn: ({ signal }) => getJobChangeConfig({ signal }),
  });
  const kit = useQuery({
    queryKey: ['keep-in-touch-config'],
    queryFn: ({ signal }) => getKeepInTouchConfig({ signal }),
  });
  const invites = useQuery({
    queryKey: ['invitations-config'],
    queryFn: ({ signal }) => getInvitationsConfig({ signal }),
  });

  const sendOn = send.data?.send_enabled === true;
  const channels = send.data?.channels ?? [];
  return (
    <div className="unified-settings-grid">
      <UnifiedSettingCard title="Send path" kicker="Master">
        <div className="setup-posture">
          <PostureRow
            label="Outbound send"
            // R22-23 review (HIGH) — only a SUCCESSFUL read is a verdict; loading/error
            // stays '—', never a false "Off".
            on={send.isSuccess ? sendOn : undefined}
            detail={
              sendOn && channels.length ? channels.map(formatChannel).join(', ') : 'no channels'
            }
          />
        </div>
      </UnifiedSettingCard>

      <UnifiedSettingCard title="LinkedIn engagement" kicker="Automation">
        <div className="setup-posture">
          <PostureRow label="Reactions" on={engage.data?.react_enabled} />
          <PostureRow label="Comments" on={engage.data?.comment_enabled} />
        </div>
      </UnifiedSettingCard>

      <UnifiedSettingCard title="Job-change DMs" kicker="Automation">
        <div className="setup-posture">
          <PostureRow label="Detect" on={jobs.data?.detect_enabled} />
          <PostureRow label="Drafts" on={jobs.data?.drafts_enabled} />
          <PostureRow label="Auto-draft" on={jobs.data?.autodraft_enabled} />
          <PostureRow label="Send" on={jobs.data?.send_enabled} />
          <PostureRow label="Auto-send" on={jobs.data?.autosend_enabled} />
        </div>
      </UnifiedSettingCard>

      <UnifiedSettingCard title="Keep in touch" kicker="Automation">
        <div className="setup-posture">
          <PostureRow label="Enabled" on={kit.data?.enabled} />
          <PostureRow label="Send" on={kit.data?.send_enabled} />
        </div>
      </UnifiedSettingCard>

      <UnifiedSettingCard title="Connection requests" kicker="Automation">
        <div className="setup-posture">
          <PostureRow label="Assess" on={invites.data?.enabled} />
          <PostureRow label="Accept / decline" on={invites.data?.send_enabled} />
        </div>
      </UnifiedSettingCard>

      <UnifiedSettingCard title="Intelligence" kicker="Analysis">
        <dl className="unified-settings-list">
          <div>
            <dt>Prompt safety</dt>
            <dd>Raw prompts and raw responses are not exposed in the UI</dd>
          </div>
        </dl>
      </UnifiedSettingCard>
    </div>
  );
}

/** R23 — live channel-health (replaces the static badge stack). */
function UnifiedChannelHealthSection() {
  const health = useQuery({
    queryKey: ['channel-health'],
    queryFn: ({ signal }) => getChannelHealth({ signal }),
    refetchInterval: 120_000,
  });
  return (
    <div className="unified-settings-grid">
      {health.isLoading ? (
        <UnifiedSettingCard title="Channels" kicker="Connectors">
          <p className="unified-side-note">Checking connectors…</p>
        </UnifiedSettingCard>
      ) : health.isError ? (
        <UnifiedSettingCard title="Channels" kicker="Connectors">
          <p className="unified-side-note">Could not read channel health.</p>
        </UnifiedSettingCard>
      ) : (
        (health.data?.channels ?? []).map((c) => {
          // R22-23 review — match the canonical health derivation (health-status.ts /
          // HealthDrawer): connection = connected && healthy; real-time is DEGRADED
          // whenever the stale signal fires (it wins over an unknown webhook), and only
          // an unknown subscription with no stale signal is the neutral '—'.
          const connectedHealthy = c.connected && c.healthy;
          const degraded = c.webhook_subscribed === false || c.realtime_stale === true;
          const realtimeUnknown = !degraded && c.webhook_subscribed === null;
          const status = !connectedHealthy
            ? 'Disconnected — reconnect in Unipile'
            : c.webhook_subscribed === false
              ? 'No real-time webhook — ~15-min safety-net'
              : c.realtime_stale === true
                ? 'Real-time delivery stalled'
                : 'Healthy';
          const realtimeClass = realtimeUnknown ? 'unknown' : degraded ? 'off' : 'on';
          return (
            <UnifiedSettingCard key={c.channel} title={formatChannel(c.channel)} kicker="Connector">
              <div className="setup-posture">
                <PostureRow label="Connection" on={connectedHealthy} detail={status} />
                <div className="setup-posture-row">
                  <span className="setup-posture-label">Real-time</span>
                  <span className={`setup-posture-state ${realtimeClass}`}>
                    {realtimeUnknown ? '—' : degraded ? 'Degraded' : 'Live'}
                  </span>
                </div>
              </div>
              <span className="unified-channel-stack">
                <ChannelBadge channel={c.channel} size="xs" />
              </span>
            </UnifiedSettingCard>
          );
        })
      )}
    </div>
  );
}

/** R23 — About (R26 removed the legacy-console link; Classic is retired). */
function UnifiedAboutSection() {
  return (
    <div className="unified-settings-grid">
      <UnifiedSettingCard title="Operator mode" kicker="Access">
        <dl className="unified-settings-list">
          <div>
            <dt>Interface</dt>
            <dd>Mailroom (redesigned)</dd>
          </div>
          <div>
            <dt>Auth</dt>
            <dd>{AUTH_MODE}</dd>
          </div>
        </dl>
      </UnifiedSettingCard>
    </div>
  );
}

/** R23b — passkey enrolment (moved off Classic) + the masked enrolled-passkey list. */
function UnifiedSecuritySection() {
  const qc = useQueryClient();
  const supported = passkeySupported();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const list = useQuery({
    queryKey: ['passkeys'],
    queryFn: ({ signal }) => listPasskeys({ signal }),
    retry: false,
  });
  const notEnabled = list.isError && (list.error as ApiError)?.status === 503;

  async function onEnroll() {
    if (busy) return;
    setStatus(null);
    setBusy(true);
    try {
      await registerPasskey(label.trim() || undefined);
      setLabel('');
      setStatus({ kind: 'ok', msg: 'Passkey added. You can now sign in with it.' });
      void qc.invalidateQueries({ queryKey: ['passkeys'] });
    } catch (err) {
      if (err instanceof ApiError && err.status === 503)
        setStatus({ kind: 'err', msg: 'Passkeys are not enabled on the server.' });
      else if (err instanceof ApiError && err.status === 409)
        setStatus({ kind: 'err', msg: 'This authenticator is already registered.' });
      else if (err instanceof ApiError && err.status === 401)
        // R22-23 review — parity with the Classic panel: a stale session is recoverable.
        setStatus({ kind: 'err', msg: 'Session expired — sign in again, then retry.' });
      else if (err instanceof DOMException && err.name === 'NotAllowedError')
        setStatus(null); // operator dismissed the OS prompt
      else setStatus({ kind: 'err', msg: 'Could not add passkey.' });
    } finally {
      setBusy(false);
    }
  }

  const creds = list.data?.credentials ?? [];
  return (
    <div className="unified-settings-grid">
      <UnifiedSettingCard title="Passkeys" kicker="Phishing-resistant sign-in">
        <p className="unified-side-note">
          Add a passkey (Touch ID, Windows Hello, or a security key). Your password keeps working as
          a fallback.
        </p>
        {notEnabled ? (
          <p className="unified-side-note">Passkeys are not enabled on the server.</p>
        ) : supported ? (
          <div className="setup-passkey-enrol">
            <input
              type="text"
              className="queue-search"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. MacBook Touch ID)"
              aria-label="passkey label"
              maxLength={120}
              disabled={busy}
            />
            <button
              type="button"
              className="unified-ghost-btn"
              data-testid="setup-add-passkey"
              onClick={onEnroll}
              disabled={busy}
            >
              {busy ? 'Waiting for device…' : 'Add passkey'}
            </button>
          </div>
        ) : (
          <p className="unified-side-note">This browser does not support passkeys.</p>
        )}
        {status ? (
          <p className={`unified-side-note ${status.kind === 'err' ? 'setup-err' : 'setup-ok'}`}>
            {status.msg}
          </p>
        ) : null}
      </UnifiedSettingCard>
      <UnifiedSettingCard title="Enrolled passkeys" kicker="Devices">
        {list.isLoading ? (
          <p className="unified-side-note">Loading…</p>
        ) : notEnabled ? (
          <p className="unified-side-note">—</p>
        ) : creds.length === 0 ? (
          <p className="unified-side-note">No passkeys enrolled yet.</p>
        ) : (
          <ul className="setup-passkey-list" data-testid="setup-passkey-list">
            {creds.map((c) => (
              <li key={c.id}>
                <strong>{c.label ?? 'Passkey'}</strong>
                <span className="setup-posture-detail">{c.id}</span>
                {c.last_used_at ? <time>{formatRelative(c.last_used_at)}</time> : null}
              </li>
            ))}
          </ul>
        )}
      </UnifiedSettingCard>
    </div>
  );
}

/** R23b — new-message sound toggle (the topbar control surfaced as a real setting). */
function UnifiedNotificationsSection() {
  const on = useSoundEnabled();
  return (
    <div className="unified-settings-grid">
      <UnifiedSettingCard title="New-message sound" kicker="Notifications">
        <p className="unified-side-note">
          Play a chime when a new actionable message arrives. Stored in this browser.
        </p>
        <button
          type="button"
          className="unified-ghost-btn"
          data-testid="setup-sound-toggle"
          aria-pressed={on}
          onClick={() => {
            const next = !on;
            setSoundEnabled(next);
            if (next) primeAudio(); // unlock audio inside this user gesture
          }}
        >
          {on ? '🔔 Sound on — mute' : '🔕 Sound off — enable'}
        </button>
      </UnifiedSettingCard>
    </div>
  );
}

/** R23b — archived (soft-deleted) contacts, reversible. Restore re-adds to the book. */
function UnifiedArchivedSection() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['contacts', 'archived'],
    // R22-23 review — match the address book's whole-book cap (5000) so a large
    // archive isn't silently truncated at 500.
    queryFn: ({ signal }) => listContacts({ archived: 'archived', limit: 5000, signal }),
  });
  const restore = useMutation({
    mutationFn: (id: string) => setContactArchived(id, false),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  const items = list.data?.items ?? [];
  return (
    <div className="unified-settings-grid">
      <UnifiedSettingCard title="Archived contacts" kicker="Reversible delete">
        {list.isLoading ? (
          <p className="unified-side-note">Loading…</p>
        ) : items.length === 0 ? (
          <p className="unified-side-note">No archived contacts.</p>
        ) : (
          <ul className="setup-archived-list" data-testid="setup-archived-list">
            {items.map((c) => (
              <li key={c.id}>
                <strong>{c.display_name ?? 'Unknown contact'}</strong>
                <button
                  type="button"
                  className="unified-ghost-btn"
                  onClick={() => restore.mutate(c.id)}
                  disabled={restore.isPending}
                >
                  ♻ Restore
                </button>
              </li>
            ))}
          </ul>
        )}
        {restore.isError ? (
          <p className="unified-side-note" data-testid="setup-restore-error" role="alert">
            Restore failed — try again.
          </p>
        ) : null}
      </UnifiedSettingCard>
    </div>
  );
}

/**
 * batch-288 — MS Outlook calendar import (Phase 1). Upload a CSV export → preview
 * (parse + hybrid-match, no writes) → import (upsert). Linked meetings then feed the
 * contact timeline + the keep-in-touch clock. Gated on CALENDAR_IMPORT_ENABLED;
 * loading/error renders neutral (never a confident "off"). Read-only — nothing sent.
 */
function UnifiedCalendarImportSection() {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['calendar-config'],
    queryFn: ({ signal }) => getCalendarConfig({ signal }),
    staleTime: 30_000,
  });
  const stats = useQuery({
    queryKey: ['calendar-stats'],
    queryFn: ({ signal }) => getCalendarStats({ signal }),
    enabled: cfg.data?.enabled === true,
  });
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CalendarImportResultDto | null>(null);
  const [committed, setCommitted] = useState<CalendarImportResultDto | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const previewM = useMutation({
    mutationFn: (f: File) => previewCalendarImport(f),
    onSuccess: (r) => {
      setPreview(r);
      setCommitted(null);
      setErr(null);
    },
    onError: (e) => setErr(serverErrorMessage(e, 'preview failed')),
  });
  const commitM = useMutation({
    mutationFn: (f: File) => commitCalendarImport(f),
    onSuccess: (r) => {
      setCommitted(r);
      setPreview(null);
      setErr(null);
      void qc.invalidateQueries({ queryKey: ['calendar-stats'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e) => setErr(serverErrorMessage(e, 'import failed')),
  });

  if (cfg.isSuccess && !cfg.data.enabled) {
    return (
      <div className="unified-settings-grid">
        <UnifiedSettingCard title="Calendar import" kicker="Integration">
          <p className="unified-side-note">
            Calendar import is off in your operator configuration (set
            <code> CALENDAR_IMPORT_ENABLED</code> to enable).
          </p>
        </UnifiedSettingCard>
      </div>
    );
  }
  if (!cfg.isSuccess) {
    return (
      <div className="unified-settings-grid">
        <UnifiedSettingCard title="Calendar import" kicker="Integration">
          <p className="unified-side-note">
            {cfg.isPending ? 'Loading…' : 'Could not load configuration.'}
          </p>
        </UnifiedSettingCard>
      </div>
    );
  }

  const summary = (preview ?? committed)?.summary ?? null;

  return (
    <div className="unified-settings-grid">
      <UnifiedSettingCard title="Calendar import" kicker="Integration">
        <p className="unified-side-note">
          Upload an Outlook calendar CSV (File → Open &amp; Export → Import/Export → Export to a
          file → Comma Separated Values). Meetings link to your contacts and feed the timeline +
          keep-in-touch. Read-only — nothing is sent.
        </p>
        {stats.data ? (
          <p className="unified-side-note" data-testid="calendar-stats">
            {stats.data.total_events} meetings imported · {stats.data.linked_contacts} contacts
            linked · {stats.data.unmatched_attendees} unmatched
            {stats.data.last_import_at
              ? ` · last import ${formatRelative(stats.data.last_import_at)}`
              : ''}
          </p>
        ) : null}
        <div className="flex flex-col gap-2 pt-1">
          <input
            type="file"
            accept=".csv,text/csv"
            data-testid="calendar-file-input"
            disabled={previewM.isPending || commitM.isPending}
            className="text-[11px]"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              // Reset the input so re-selecting the SAME file (e.g. after an error, or an
              // updated export saved under the same name) fires onChange again. The File
              // is already captured in `f` (review finding 6).
              e.target.value = '';
              setFile(f);
              setPreview(null);
              setCommitted(null);
              setErr(null);
              if (f) previewM.mutate(f);
            }}
          />
          {previewM.isPending ? <p className="unified-side-note">Analysing the file…</p> : null}
          {err ? (
            <p className="unified-side-note" role="alert">
              ⚠ {err}
            </p>
          ) : null}
          {summary ? (
            <div className="flex flex-col gap-2" data-testid="calendar-import-summary">
              <p className="text-[12px]">
                <strong>{summary.event_count}</strong> events ·{' '}
                <strong>{summary.linked_events}</strong> link to contacts ·{' '}
                {summary.unmatched_people} unmatched people · {summary.new_emails} new emails
              </p>
              {summary.warnings.length > 0 ? (
                <p className="unified-side-note">⚠ {summary.warnings.join('; ')}</p>
              ) : null}
              {preview && file ? (
                <button
                  type="button"
                  className="unified-ghost-btn"
                  data-testid="calendar-commit-btn"
                  disabled={commitM.isPending}
                  onClick={() => commitM.mutate(file)}
                >
                  {commitM.isPending ? 'Importing…' : `Import ${summary.event_count} meetings`}
                </button>
              ) : null}
              {committed ? (
                <p className="unified-side-note">
                  ✓ Imported — {committed.summary.linked_events} meetings linked to contacts.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </UnifiedSettingCard>
      {committed && committed.unmatched.length > 0 ? (
        <UnifiedSettingCard title="Confirm matches" kicker="Optional">
          <p className="unified-side-note">
            These meeting attendees didn’t auto-match. Link each to a contact (remembered for future
            imports), or mark “not a contact”.
          </p>
          <CalendarUnmatchedList items={committed.unmatched} />
        </UnifiedSettingCard>
      ) : null}
    </div>
  );
}

/** The confirm-matches list — one row per unmatched attendee, reusing ContactPicker. */
function CalendarUnmatchedList({ items }: { items: CalendarUnmatchedItem[] }) {
  const qc = useQueryClient();
  const [resolved, setResolved] = useState<Set<string>>(() => new Set());
  const link = useMutation({
    mutationFn: (b: {
      raw_key: string;
      key_type: 'name' | 'email';
      contact_id: string | null;
      status: 'confirmed' | 'ignored';
    }) => confirmCalendarAlias(b),
    onSuccess: (_r, b) => {
      setResolved((s) => new Set(s).add(b.raw_key));
      void qc.invalidateQueries({ queryKey: ['calendar-stats'] });
    },
  });
  const pending = items.filter((i) => !resolved.has(i.raw_key));
  if (pending.length === 0) return <p className="unified-side-note">All set.</p>;
  return (
    <ul className="flex flex-col gap-3" data-testid="calendar-unmatched-list">
      {link.isError ? (
        <li className="unified-side-note" role="alert">
          Link failed — try again.
        </li>
      ) : null}
      {pending.slice(0, 50).map((i) => (
        <li key={i.raw_key} className="flex flex-col gap-1">
          <div className="text-[12px]">
            <strong>{i.display || i.raw_key}</strong>{' '}
            <span className="unified-side-note">
              · {i.count} meeting{i.count === 1 ? '' : 's'}
            </span>
          </div>
          <ContactPicker
            excludeContactId=""
            placeholder={`Link "${i.display || i.raw_key}" to a contact…`}
            disabled={link.isPending}
            onPick={(contactId) =>
              link.mutate({
                raw_key: i.raw_key,
                key_type: i.key_type,
                contact_id: contactId,
                status: 'confirmed',
              })
            }
          />
          <button
            type="button"
            className="unified-ghost-btn self-start"
            disabled={link.isPending}
            onClick={() =>
              link.mutate({
                raw_key: i.raw_key,
                key_type: i.key_type,
                contact_id: null,
                status: 'ignored',
              })
            }
          >
            Not a contact
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * batch-133 — the operator's LinkedIn "engagement interests": what topics/sectors/
 * post-types are worth engaging vs noise. Feeds the LI Engagement relevance
 * judgement so the hub hides professionally-irrelevant ("rubbish") posts.
 */
function UnifiedEngagementInterestsCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['engagement-interests'],
    queryFn: () => getEngagementInterests(),
  });
  const [text, setText] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const serverValue = query.data?.engagement_interests ?? '';
  const value = text ?? serverValue;

  const save = useMutation({
    mutationFn: () => putEngagementInterests(value),
    onSuccess: (res) => {
      setText(res.engagement_interests);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['engagement-interests'] });
    },
  });

  return (
    <UnifiedSettingCard title="LinkedIn engagement interests" kicker="Relevance filter">
      <p className="unified-card-note">
        What's worth engaging on vs noise. The LI Engagement hub uses this to hide
        professionally-irrelevant posts. e.g. “Relevant: AI regulation, fintech infrastructure,
        ADGM/UAE policy. Noise: motivational posts, job-change announcements, ‘humbled to share’,
        giveaways.”
      </p>
      {/* QA M3 — the editor stays READ-ONLY until the real stored value is on
          screen. On a failed GET the field would otherwise show '' + enabled,
          and a Save would silently overwrite the operator's stored interests
          with the phantom-empty baseline. */}
      {query.isError && query.data === undefined ? (
        <span className="unified-learning-warn">
          Couldn't load the saved interests —{' '}
          <button
            type="button"
            className="unified-inline-link"
            onClick={() => void query.refetch()}
          >
            retry
          </button>{' '}
          before editing.
        </span>
      ) : null}
      <textarea
        data-testid="engagement-interests-input"
        rows={4}
        value={value}
        disabled={query.data === undefined || save.isPending}
        onChange={(e) => {
          setText(e.target.value);
          setSaved(false);
        }}
        placeholder="Relevant: …  Noise: …"
        className="unified-styleguide-textarea"
      />
      <div className="unified-card-actions">
        <button
          type="button"
          className="unified-btn"
          data-testid="engagement-interests-save"
          disabled={query.data === undefined || save.isPending || value === serverValue}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save interests'}
        </button>
        {saved && value === serverValue ? (
          <span className="unified-learning-ok">Saved — applies to new relevance checks.</span>
        ) : null}
        {save.isError ? <span className="unified-learning-warn">Save failed.</span> : null}
      </div>
    </UnifiedSettingCard>
  );
}

/**
 * batch-62 — the operator's persistent global "AI style guide". Free-text
 * standing rules applied to EVERY draft (e.g. casing, banned punctuation).
 * Distinct from the per-thread Guidance+Refine (one-shot). Saving busts the
 * analysis cache, so the next Generate/Refine reflects the new rules.
 */
function UnifiedStyleGuideCard() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['style-guide'], queryFn: () => getStyleGuide() });
  const [text, setText] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Seed the editor from the server value once it loads (and only then).
  const serverValue = query.data?.style_guide ?? '';
  const value = text ?? serverValue;

  const save = useMutation({
    mutationFn: () => putStyleGuide(value),
    onSuccess: (res) => {
      setText(res.style_guide);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['style-guide'] });
    },
  });

  return (
    <UnifiedSettingCard title="AI style guide" kicker="Standing rules">
      <p className="unified-card-note">
        Standing rules applied to <strong>every</strong> draft (e.g. “Capitalize the first letter of
        each sentence. Never use em-dashes.”). Per-thread Guidance + Refine is for one-offs.
      </p>
      {/* QA M3 — read-only until the stored rules load; a failed GET must not let
          a Save overwrite the operator's standing style guide with a blank. */}
      {query.isError && query.data === undefined ? (
        <span className="unified-learning-warn">
          Couldn't load the saved rules —{' '}
          <button
            type="button"
            className="unified-inline-link"
            onClick={() => void query.refetch()}
          >
            retry
          </button>{' '}
          before editing.
        </span>
      ) : null}
      <textarea
        data-testid="style-guide-input"
        rows={4}
        value={value}
        disabled={query.data === undefined || save.isPending}
        onChange={(e) => {
          setText(e.target.value);
          setSaved(false);
        }}
        placeholder="One rule per line…"
        className="unified-styleguide-textarea"
      />
      <div className="unified-card-actions">
        <button
          type="button"
          className="unified-btn"
          data-testid="style-guide-save"
          disabled={query.data === undefined || save.isPending || value === serverValue}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save rules'}
        </button>
        {saved && value === serverValue ? (
          <span className="unified-learning-ok">Saved — applies to new drafts.</span>
        ) : null}
        {save.isError ? <span className="unified-learning-warn">Save failed.</span> : null}
      </div>
    </UnifiedSettingCard>
  );
}

/**
 * Learning status — makes the "is the loop being fed?" gap visible. The system
 * learns your voice from proposal→final EDIT deltas; accepting AI drafts
 * verbatim teaches nothing new. Surfaces the accept rate + the two levers
 * (edits captured, per-contact preferences) with guidance.
 */
function UnifiedLearningCard() {
  const query = useQuery({
    queryKey: ['learning-metrics'],
    queryFn: ({ signal }) => getLearningMetrics({ signal }),
    staleTime: 60_000,
  });

  let body: ReactNode;
  if (query.isPending) {
    body = <LoadingState label="Loading learning status" />;
  } else if (query.isError) {
    body = <p className="unified-learning-warn">Could not load learning status.</p>;
  } else {
    const m = query.data;
    const accepted = m.by_confidence.reduce((n, b) => n + b.accepted, 0);
    const edited = m.by_confidence.reduce((n, b) => n + b.edited, 0);
    const acceptRate = m.total > 0 ? Math.round((accepted / m.total) * 100) : null;
    const fed = m.edits_captured > 0 || m.contact_preferences > 0;
    body = (
      <>
        <dl className="unified-settings-list">
          <div>
            <dt>AI drafts (since model)</dt>
            <dd>
              {m.total} — {accepted} kept as-is, {edited} edited
              {acceptRate !== null ? ` (${acceptRate}% verbatim)` : ''}
            </dd>
          </div>
          <div>
            <dt>Edit deltas captured</dt>
            <dd data-testid="learning-edits-captured">{m.edits_captured}</dd>
          </div>
          <div>
            <dt>Per-contact preferences</dt>
            <dd>{m.contact_preferences}</dd>
          </div>
        </dl>
        <p className={fed ? 'unified-learning-ok' : 'unified-learning-warn'}>
          {fed
            ? 'The learning loop is being fed — keep editing drafts and setting contact preferences to sharpen it.'
            : 'Nothing is teaching the model yet. Edit AI drafts before saving (even small tweaks) — each edit is captured and tunes future drafts to your voice. Accepting verbatim teaches nothing.'}
        </p>
      </>
    );
  }

  return (
    <UnifiedSettingCard title="Learning" kicker="Teach the model">
      {body}
    </UnifiedSettingCard>
  );
}

/**
 * R14b (#9) — the visible Clear-no-reply job: a determinate "N of M" progress bar
 * while running, a result chip when done, a dismissible error otherwise.
 */
export function ClearNoReplyJob({ job, onDismiss }: { job: ClearJob; onDismiss: () => void }) {
  if (job.phase === 'idle') return null;
  if (job.phase === 'error') {
    return (
      <span className="unified-clear-job is-error" role="status" data-testid="clear-job">
        Clear failed: {job.message}
        <button
          type="button"
          className="unified-clear-dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          ✕
        </button>
      </span>
    );
  }
  if (job.phase === 'done') {
    return (
      <span className="unified-clear-job is-done" role="status" data-testid="clear-job">
        Cleared {job.cleared}
        <button
          type="button"
          className="unified-clear-dismiss"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          ✕
        </button>
      </span>
    );
  }
  // R14b review (#8) — until the first chunk returns, `total` is 0: render an
  // INDETERMINATE progressbar (no value attrs, no degenerate max==min==0) rather
  // than a misleading "0 of 0". #14 — role="status" already implies aria-live.
  const known = job.total > 0;
  const pct = known ? Math.min(100, Math.round((job.cleared / job.total) * 100)) : 0;
  return (
    <span className="unified-clear-job" role="status" data-testid="clear-job">
      <span className="unified-clear-label">
        {known ? `Clearing ${job.cleared} of ${job.total}` : 'Clearing…'}
      </span>
      <span
        className="unified-clear-bar"
        role="progressbar"
        aria-label="Clearing no-reply threads"
        {...(known
          ? { 'aria-valuemin': 0, 'aria-valuemax': job.total, 'aria-valuenow': job.cleared }
          : {})}
      >
        <span
          className={known ? 'unified-clear-bar-fill' : 'unified-clear-bar-fill is-indeterminate'}
          style={known ? { width: `${pct}%` } : undefined}
        />
      </span>
    </span>
  );
}

function ThreadListSurface({
  query,
  threads,
  selectedId,
  onSelect,
  emptyTitle,
  emptyHint,
}: {
  query: ReturnType<typeof useInfiniteQuery<unknown, Error, { pages: ThreadsPage[] }>>;
  threads: ThreadListItemDto[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  emptyTitle: string;
  emptyHint: string;
}) {
  // R14b review — hooks BEFORE the early returns. Stable onReachEnd (else every
  // poll / SSE re-render re-fires fetchNextPage when parked at the bottom).
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const onReachEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const selectedIndex = useMemo(
    () => (selectedId ? threads.findIndex((t) => t.id === selectedId) : -1),
    [threads, selectedId],
  );

  if (query.isPending) {
    return (
      <div className="unified-thread-list">
        <LoadingState label="Loading inbox" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="unified-thread-list">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  if (threads.length === 0) {
    return (
      <div className="unified-thread-list">
        <EmptyState title={emptyTitle} hint={emptyHint} />
      </div>
    );
  }
  // R14b — virtualized list IS the scroll container; infinite pages load as the
  // operator scrolls (the old "Load more" button is gone).
  return (
    <VirtualList
      className="unified-thread-list"
      testId="unified-thread-vlist"
      ariaLabel="Conversations"
      items={threads}
      getKey={(t) => t.id}
      estimateSize={56}
      onReachEnd={onReachEnd}
      scrollToIndex={selectedIndex >= 0 ? selectedIndex : null}
      renderItem={(thread) => (
        <UnifiedThreadRow thread={thread} selected={thread.id === selectedId} onSelect={onSelect} />
      )}
    />
  );
}

type ThreadsPage = { items: ThreadListItemDto[]; next_cursor: string | null };

function ModeButton({
  mode,
  value,
  label,
  count,
  onSelect,
}: {
  mode: Mode;
  value: Mode;
  label: string;
  count: number | undefined;
  onSelect: (mode: Mode) => void;
}) {
  const active = mode === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={`unified-mode-${value}`}
      className={active ? 'active' : ''}
      onClick={() => onSelect(value)}
    >
      <span>{label}</span>
      {typeof count === 'number' ? <strong>{count}</strong> : null}
    </button>
  );
}

function ChannelTabs({
  channel,
  onChange,
}: {
  channel: ChannelFilter;
  onChange: (value: ChannelFilter) => void;
}) {
  return (
    <div className="unified-channel-row" role="tablist" aria-label="Channel filter">
      {CHANNEL_OPTIONS.map((option) => (
        <button
          key={option.value ?? 'all'}
          type="button"
          role="tab"
          aria-selected={channel === option.value}
          data-testid={`unified-channel-${option.value ?? 'all'}`}
          className={
            channel === option.value ? 'unified-channel-pill active' : 'unified-channel-pill'
          }
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function UnifiedThreadRow({
  thread,
  selected,
  onSelect,
}: {
  thread: ThreadListItemDto;
  selected: boolean;
  onSelect: (id: string | null) => void;
}) {
  const contact = threadLabel(thread.participants, thread.subject, thread.channel);
  const snippet = thread.last_message?.snippet ?? thread.subject ?? 'No preview available';
  const ts = thread.last_message?.source_timestamp ?? thread.last_message_at;
  // R14 — STATUS-derived "Needs reply" (replaces the direction dot): the actionable
  // statuses. Mute/snooze get their own row flags.
  const muted = thread.muted || thread.counterparty_muted;
  const snoozeMs = toMs(thread.snooze_until);
  const snoozed = snoozeMs != null && snoozeMs > Date.now();
  // QA L1 — a muted/snoozed thread is NOT actionable (the server excludes it from the
  // needs-reply surfaces), so in the "All" view it must read as the quiet glyph, not
  // the "Needs reply" pill. (Was status-only, which mislabelled muted/snoozed rows.)
  const needsReply =
    (thread.review_status === 'new' || thread.review_status === 'needs_review') &&
    !muted &&
    !snoozed;
  // Inbox-rebuild Phase 1 — calm row: avatar · name/time · channel-dot/snippet · ONE
  // status indicator (a needs-reply dot, else a quiet snoozed/muted glyph, with the
  // row dimmed). Replaces the stacked badges/pills.
  const cls = ['unified-thread-row', 'unified-inbox-row'];
  if (selected) cls.push('selected');
  if (needsReply) cls.push('needs-reply');
  if (!needsReply && (muted || snoozed)) cls.push('row-quiet');

  // R14b — VirtualList provides the measured <li>. R15 — the row is a select
  // <button> PLUS a sibling hover-revealed TriageActions cluster (sibling, not
  // nested, so its clicks don't select the row). The wrap is position:relative.
  return (
    <div className="unified-row-wrap">
      <button
        type="button"
        data-testid="unified-thread-row"
        aria-current={selected ? 'true' : undefined}
        className={cls.join(' ')}
        onClick={() => onSelect(thread.id)}
      >
        <span className="urow-ava" aria-hidden="true">
          {initialsOf(contact.label)}
        </span>
        <span className="urow-main">
          <span className="urow-top">
            <strong className="urow-name">{contact.label}</strong>
            <time className="urow-time">{formatRelative(ts)}</time>
          </span>
          <span className="urow-sub">
            <span className={`urow-chdot ch-${thread.channel}`} aria-hidden="true" />
            <span className="urow-snippet">{snippet}</span>
          </span>
        </span>
        <span className="urow-status">
          {needsReply ? (
            <span
              className="urow-need"
              data-testid="needs-reply-pill"
              role="img"
              aria-label="Needs reply"
              title="Needs reply"
            />
          ) : null}
          {snoozed ? (
            <span
              className="urow-flag-ico"
              data-testid="row-snoozed"
              role="img"
              aria-label="Snoozed"
              title="Snoozed"
            >
              <SnoozeGlyph />
            </span>
          ) : muted ? (
            <span
              className="urow-flag-ico"
              data-testid="row-muted"
              role="img"
              aria-label="Muted"
              title="Muted — hidden from Needs reply"
            >
              <MuteGlyph />
            </span>
          ) : null}
        </span>
      </button>
      <div className="unified-row-triage">
        <TriageActions threadId={thread.id} variant="row" tabbable={selected} />
      </div>
    </div>
  );
}

function SnoozeGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function MuteGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4z" />
      <path d="m22 9-6 6M16 9l6 6" />
    </svg>
  );
}

function UnifiedFollowupRow({
  item,
  selected,
  onSelect,
}: {
  item: FollowupListItemDto;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const contact = threadLabel(item.participants, item.subject, item.channel);
  const label = REASON_LABEL[item.followup_reason];
  const snippet = item.last_message?.snippet ?? item.subject ?? 'No preview available';

  return (
    <li>
      <button
        type="button"
        data-testid="unified-followup-row"
        aria-current={selected ? 'true' : undefined}
        className={selected ? 'unified-thread-row selected' : 'unified-thread-row'}
        onClick={() => onSelect(item.id)}
      >
        <span className="unified-reply-dot active" />
        <span className="unified-thread-main">
          <span className="unified-thread-line">
            <strong>{contact.label}</strong>
            <time>{formatRelative(item.last_message_at)}</time>
          </span>
          <span className="unified-thread-meta">
            <ChannelBadge channel={item.channel} size="xs" />
            <span className="unified-small-pill">{label}</span>
          </span>
          <span className="unified-thread-snippet">{snippet}</span>
        </span>
      </button>
    </li>
  );
}

function UnifiedCandidateRow({
  candidate,
  onSelectContact,
  onConfirm,
  onReject,
  pending,
}: {
  candidate: CandidateDto;
  onSelectContact: (id: string) => void;
  onConfirm: () => void;
  onReject: () => void;
  pending: boolean;
}) {
  return (
    <li className="unified-candidate-row" data-testid="unified-candidate-row">
      <div className="unified-candidate-sides">
        <CandidateSide side={candidate.contact_a} onSelect={onSelectContact} />
        <span className="unified-match-arrow">↔</span>
        <CandidateSide side={candidate.contact_b} onSelect={onSelectContact} />
        <span className="unified-count-pill">{candidate.confidence}%</span>
      </div>
      <div className="unified-reason-row">
        {candidate.reasons.map((reason) => (
          <span key={reason}>{reason.replaceAll('_', ' ')}</span>
        ))}
      </div>
      <div className="unified-row-actions">
        <button type="button" disabled={pending} onClick={onConfirm}>
          Confirm match
        </button>
        <button type="button" disabled={pending} onClick={onReject}>
          Reject
        </button>
        <time>{formatRelative(candidate.created_at)}</time>
      </div>
    </li>
  );
}

function CandidateSide({
  side,
  onSelect,
}: {
  side: CandidateDto['contact_a'];
  onSelect: (id: string) => void;
}) {
  return (
    <button type="button" className="unified-candidate-side" onClick={() => onSelect(side.id)}>
      <strong>{side.display_name ?? 'Unknown'}</strong>
      <span>
        {side.channels.map((channel) => (
          <ChannelBadge key={channel} channel={channel} size="xs" />
        ))}
      </span>
    </button>
  );
}

function UnifiedRightRail({
  threadId,
  onSelectContact,
}: {
  threadId: string | null;
  onSelectContact: (id: string) => void;
}) {
  const query = useQuery({
    queryKey: ['thread', threadId],
    queryFn: ({ signal }) => getThread(threadId ?? '', { signal }),
    enabled: threadId !== null,
    refetchInterval: LIVE_POLL_MS,
  });

  if (threadId === null) {
    return (
      <aside className="unified-context" aria-label="Context">
        <EmptyState title="Context" hint="Select a thread to see AI and contact cues." />
      </aside>
    );
  }
  if (query.isPending) {
    return (
      <aside className="unified-context" aria-label="Context">
        <LoadingState label="Loading context" />
      </aside>
    );
  }
  if (query.isError) {
    return (
      <aside className="unified-context" aria-label="Context">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </aside>
    );
  }

  return (
    <aside className="unified-context" aria-label="Context">
      <UnifiedContactCard thread={query.data} onSelectContact={onSelectContact} />
      <UnifiedAiCard thread={query.data} />
    </aside>
  );
}

function UnifiedAdminMenu({ onBack }: { onBack: () => void }) {
  return (
    <section className="unified-inbox" aria-label="Admin menu">
      <div className="unified-inbox-header">
        {/* R22-23 review — Identity matches is reached from Contacts (no rail item);
            give it an explicit way back so it isn't a navigational dead-end. */}
        <button type="button" className="ab-back" data-testid="candidates-back" onClick={onBack}>
          ‹ Contacts
        </button>
        <div className="unified-page-card compact">
          <div className="unified-side-kicker">Review area</div>
          <h2>Contact hygiene</h2>
          <p>Review duplicates, junk contacts, and attribution problems in the panel.</p>
        </div>
      </div>
    </section>
  );
}

function UnifiedAdminRail() {
  return (
    <aside className="unified-context" aria-label="Admin context">
      <section className="unified-side-section">
        <div className="unified-side-kicker">What confirming does</div>
        <h2>Reversible merge</h2>
        <p>
          Confirming a pair merges the two contacts into one. It is reversible from the contact's
          merge history.
        </p>
      </section>
      <section className="unified-side-section">
        <div className="unified-side-kicker">Privacy</div>
        <h2>Reason codes only</h2>
        <p>Rows show display labels, channels, confidence, and reason labels.</p>
      </section>
    </aside>
  );
}

function UnifiedSetupMenu({
  section,
  onSection,
}: {
  section: SetupSection;
  onSection: (s: SetupSection) => void;
}) {
  return (
    <section className="unified-inbox" aria-label="Setup sections">
      <div className="unified-inbox-header">
        <div className="unified-page-card compact">
          <div className="unified-side-kicker">Setup</div>
          <h2>System</h2>
        </div>
      </div>
      <nav className="setup-nav" aria-label="Setup sections">
        {SETUP_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`setup-nav-item${section === s.id ? ' active' : ''}`}
            aria-current={section === s.id ? 'page' : undefined}
            data-testid={`setup-nav-${s.id}`}
            onClick={() => onSection(s.id)}
          >
            <span className="setup-nav-kicker">{s.kicker}</span>
            <span className="setup-nav-label">{s.label}</span>
          </button>
        ))}
      </nav>
    </section>
  );
}

function UnifiedStatusRail() {
  // Live send posture from the server allow-list — never hard-coded. (R1 honesty
  // purge: this rail used to claim "Dispatch — Still disabled" beside the live
  // "Enabled · LinkedIn, Email…" card two panes over.)
  const sendConfig = useQuery({
    queryKey: ['send-config'],
    queryFn: () => getSendConfig(),
    staleTime: 30_000,
  });
  const sendEnabled = sendConfig.data?.send_enabled === true;
  const channels = sendConfig.data?.channels ?? [];
  let posture: string;
  if (sendConfig.isLoading) posture = 'Checking…';
  else if (sendConfig.isError) posture = 'Status unavailable right now.';
  else if (sendEnabled && channels.length > 0)
    posture = `Sending is on for ${channels.map(formatChannel).join(', ')}.`;
  else posture = 'Sending is off — no channels are enabled.';

  return (
    <aside className="unified-context" aria-label="Setup context">
      <section className="unified-side-section">
        <div className="unified-side-kicker">Outbound</div>
        <h2>{sendEnabled ? 'Sending armed' : 'Sending off'}</h2>
        <p>{posture}</p>
      </section>
      <section className="unified-side-section">
        <div className="unified-side-kicker">Backups</div>
        <h2>Local only</h2>
        <p>Off-host backups are not configured yet — the standing data-loss risk.</p>
      </section>
    </aside>
  );
}

function UnifiedContactCard({
  thread,
  onSelectContact,
}: {
  thread: ThreadDetailDto;
  onSelectContact: (id: string) => void;
}) {
  const contact = threadLabel(thread.participants, thread.subject, thread.channel);
  const participant = thread.participants.find((p) => !p.is_self && p.contact_id);
  const facts = thread.contact_profile_facts;
  const tldr = facts?.relationship_summary ?? buildRelationshipTldr(thread);
  const contactId = participant?.contact_id ?? null;
  const profileUrl = facts?.public_profile_url ?? null;

  return (
    <section className="unified-side-section">
      <div className="unified-contact-card-head">
        <div className="unified-side-kicker">Contact</div>
        {contactId ? (
          <ProfileFactsEditorButton contactId={contactId} facts={facts} threadId={thread.id} />
        ) : null}
      </div>
      <ProfilePhoto src={facts?.profile_image_url ?? null} label={contact.label} />
      <h2>{contact.label}</h2>
      <div className="unified-side-row">
        <ChannelBadge channel={thread.channel} size="xs" />
        <StatusBadge status={thread.review_status} size="xs" />
      </div>
      <p>{thread.messages.length} messages in this conversation</p>
      <UnifiedAssignContact currentContactId={contactId} threadId={thread.id} />
      <UnifiedMuteControls thread={thread} contactId={contactId} />
      <dl className="unified-contact-facts" data-testid="unified-contact-facts">
        <div>
          <dt>Company</dt>
          <dd>{facts?.company ?? '—'}</dd>
        </div>
        <div>
          <dt>Role</dt>
          <dd>{facts?.role ?? '—'}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>{facts?.location ?? '—'}</dd>
        </div>
        <div className="unified-contact-facts-wide">
          <dt>How you know them</dt>
          <dd>{tldr ?? 'No relationship summary yet.'}</dd>
        </div>
      </dl>
      {profileUrl ? (
        <a className="unified-button-link" href={profileUrl} target="_blank" rel="noreferrer">
          Open contact
        </a>
      ) : contactId ? (
        <button type="button" onClick={() => onSelectContact(contactId)}>
          Open contact
        </button>
      ) : null}
    </section>
  );
}

/**
 * Editor for the four operator-overridable profile facts. Lives in a
 * small popover off the contact card's "Edit" button.
 *
 * Truth model (B2-H1):
 *   - Inputs are pre-filled only with values whose source is 'manual'.
 *     A field where the displayed value is imported / AI / derived
 *     starts EMPTY in the editor, with the current derived value
 *     shown underneath as a hint.
 *   - Each field tracks a `dirty` flag: only fields the operator
 *     actually touched are included in the PATCH body. Untouched
 *     fields don't round-trip, so saving without editing cannot
 *     freeze a derived guess as a permanent override.
 *   - Clearing a previously-overridden field (operator deletes the
 *     text and saves) sends `null` so the API drops the override
 *     and the displayed value reverts to the derived stack.
 */
/**
 * batch-99 — assign this conversation's sender to an existing contact, or create
 * a NEW one (name + optional company/role/location), from the Unified workspace
 * contact card. Mirrors the ProfileFactsEditorButton popover idiom (light theme,
 * unified-* classes). Reuses the existing reassign / assign-new API; both are
 * reversible (logged in contact_merges, undoable in Classic People).
 */
/** Map server refusal codes to operator-friendly text. */
function assignErrorText(message: string): string {
  if (message.includes('cannot_resolve_counterparty')) {
    if (message.includes('group'))
      return "This looks like a group chat — can't assign a single contact.";
    return "Couldn't identify the sender on this conversation. Try reopening it, or assign from the People tab.";
  }
  if (message.includes('roster_fetch_failed')) {
    return "Couldn't reach the messaging provider to identify the sender. Try again in a moment.";
  }
  return message;
}

/**
 * batch-106 — mark this conversation and/or its contact non-actionable so they
 * leave the "Needs reply" queue (they stay browsable under "All"). Two
 * independent toggles; either being set hides the thread from Needs reply.
 * Invalidates the thread + inbox/summary + contacts queries so the change is
 * reflected immediately (and live polling keeps it fresh).
 */
export function UnifiedMuteControls({
  thread,
  contactId,
}: {
  thread: ThreadDetailDto;
  contactId: string | null;
}) {
  const queryClient = useQueryClient();
  const convMuted = thread.muted === true;
  const contactMuted = thread.counterparty_muted === true;

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['thread', thread.id] }),
      queryClient.invalidateQueries({ queryKey: ['threads'] }),
      queryClient.invalidateQueries({ queryKey: ['contacts'] }),
      contactId
        ? queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
        : Promise.resolve(),
    ]);

  // batch-152 — surface mute/snooze failures (were silent fire-and-forget).
  const [ctrlErr, setCtrlErr] = useState<string | null>(null);
  const onCtrlError = (e: unknown) => setCtrlErr(e instanceof Error ? e.message : 'action failed');
  const convMutation = useMutation({
    mutationFn: (next: boolean) => setThreadMuted(thread.id, next),
    onSuccess: invalidate,
    onError: onCtrlError,
  });
  const contactMutation = useMutation({
    mutationFn: (next: boolean) => setContactMuted(contactId ?? '', next),
    onSuccess: invalidate,
    onError: onCtrlError,
  });
  // batch-149 — snooze this conversation out of Needs reply until a future time.
  const snoozeMutation = useMutation({
    mutationFn: (until: string | null) => snoozeThread(thread.id, until),
    onSuccess: () => {
      setCtrlErr(null);
      return invalidate();
    },
    onError: onCtrlError,
  });
  const busy = convMutation.isPending || contactMutation.isPending || snoozeMutation.isPending;

  const snoozedUntil = thread.snooze_until ? new Date(thread.snooze_until) : null;
  const isSnoozed = snoozedUntil !== null && snoozedUntil.getTime() > Date.now();

  return (
    <div className="unified-mute-controls" data-testid="mute-controls">
      <button
        type="button"
        className="unified-inline-link"
        data-testid="mute-conversation"
        aria-pressed={convMuted}
        disabled={busy}
        onClick={() => convMutation.mutate(!convMuted)}
      >
        {convMuted ? 'Un-mute conversation' : 'Mute conversation'}
      </button>
      {contactId ? (
        <button
          type="button"
          className="unified-inline-link"
          data-testid="mute-contact"
          aria-pressed={contactMuted}
          disabled={busy}
          onClick={() => contactMutation.mutate(!contactMuted)}
        >
          {contactMuted ? 'Un-mute contact' : 'Mute contact'}
        </button>
      ) : null}
      {convMuted || contactMuted ? (
        <p className="unified-fact-hint" data-testid="mute-hint">
          Non-actionable — hidden from “Needs reply”, still under “All”.
        </p>
      ) : null}
      <div className="unified-snooze-controls" data-testid="snooze-controls">
        {isSnoozed ? (
          <span className="unified-fact-hint" data-testid="snooze-status">
            Snoozed until {snoozedUntil?.toLocaleString()}
          </span>
        ) : null}
        {/* R15 — the ONE shared snooze control (same presets everywhere). */}
        <SnoozePopover
          replyAware
          onPick={(until) => snoozeMutation.mutate(until)}
          onUnsnooze={() => snoozeMutation.mutate(null)}
          isSnoozed={isSnoozed}
          disabled={busy}
        />
      </div>
      {ctrlErr ? (
        <p
          className="unified-fact-hint"
          data-testid="mute-snooze-error"
          style={{ color: 'var(--u-danger)' }}
        >
          {ctrlErr}
        </p>
      ) : null}
    </div>
  );
}

export function UnifiedAssignContact({
  currentContactId,
  threadId,
  onAssigned,
}: {
  currentContactId: string | null;
  threadId: string;
  /** Optional — fired after a successful (re)assign. The thread query is always
   * invalidated so the card refreshes in place; the operator stays in the
   * conversation. */
  onAssigned?: (contactId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const searchId = useId();

  const results = useQuery({
    queryKey: ['contact-picker', q],
    queryFn: ({ signal }) => listContacts({ q: q.trim(), limit: 8, signal }),
    enabled: open && mode === 'existing' && q.trim().length >= 2,
  });

  const refresh = async (newId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] }),
      queryClient.invalidateQueries({ queryKey: ['contacts'] }),
      currentContactId
        ? queryClient.invalidateQueries({ queryKey: ['contact', currentContactId] })
        : Promise.resolve(),
      newId ? queryClient.invalidateQueries({ queryKey: ['contact', newId] }) : Promise.resolve(),
    ]);
  };
  const close = () => {
    setOpen(false);
    setMode('existing');
    setQ('');
    setName('');
    setCompany('');
    setRole('');
    setLocation('');
    setError(null);
  };

  const assignExisting = useMutation({
    mutationFn: (contactId: string) =>
      assignThreadContact(threadId, { mode: 'existing', contact_id: contactId }),
    onSuccess: async (res) => {
      await refresh(res.contact_id);
      close();
      onAssigned?.(res.contact_id);
    },
    onError: (e) =>
      setError(e instanceof Error ? assignErrorText(e.message) : 'Failed to assign contact'),
  });
  const createNew = useMutation({
    mutationFn: () =>
      assignThreadContact(threadId, {
        mode: 'new',
        display_name: name.trim(),
        company: company.trim() || null,
        role: role.trim() || null,
        location: location.trim() || null,
      }),
    onSuccess: async (res) => {
      await refresh(res.contact_id);
      close();
      onAssigned?.(res.contact_id);
    },
    onError: (e) =>
      setError(e instanceof Error ? assignErrorText(e.message) : 'Failed to create contact'),
  });
  const busy = assignExisting.isPending || createNew.isPending;

  if (!open) {
    return (
      <button
        type="button"
        className="unified-inline-link"
        data-testid="assign-contact-open"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        Assign / change contact
      </button>
    );
  }

  const items = (results.data?.items ?? []).filter((c) => c.id !== currentContactId);

  return (
    <Dialog
      open
      onClose={close}
      label="Assign or change contact"
      testId="assign-contact-panel"
      className="unified-facts-popover"
    >
      <div className="unified-side-row">
        <button
          type="button"
          data-testid="assign-mode-existing"
          className={mode === 'existing' ? undefined : 'unified-secondary-button'}
          aria-pressed={mode === 'existing'}
          onClick={() => setMode('existing')}
        >
          Existing
        </button>
        <button
          type="button"
          data-testid="assign-mode-new"
          className={mode === 'new' ? undefined : 'unified-secondary-button'}
          aria-pressed={mode === 'new'}
          onClick={() => setMode('new')}
        >
          New contact
        </button>
      </div>

      {mode === 'existing' ? (
        <div className="unified-fact-row">
          <label htmlFor={searchId}>Search contacts</label>
          <input
            id={searchId}
            data-testid="assign-search"
            value={q}
            disabled={busy}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a name (min 2 chars)…"
          />
          {q.trim().length >= 2 ? (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.length === 0 ? (
                <li className="unified-fact-hint">
                  {results.isPending ? 'Searching…' : 'No matches.'}
                </li>
              ) : (
                items.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="unified-inline-link"
                      data-testid="assign-result"
                      disabled={busy}
                      onClick={() => assignExisting.mutate(c.id)}
                    >
                      {c.display_name ?? 'Unknown contact'}
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>
      ) : (
        <>
          <FactField label="Name" value={name} onChange={setName} hint={null} />
          <FactField label="Company (optional)" value={company} onChange={setCompany} hint={null} />
          <FactField label="Role (optional)" value={role} onChange={setRole} hint={null} />
          <FactField
            label="Location (optional)"
            value={location}
            onChange={setLocation}
            hint={null}
          />
        </>
      )}

      {error ? <div className="unified-form-error">{error}</div> : null}
      <div className="unified-facts-actions">
        {mode === 'new' ? (
          <button
            type="button"
            data-testid="assign-new-submit"
            disabled={busy || name.trim().length === 0}
            onClick={() => createNew.mutate()}
          >
            {createNew.isPending ? 'Creating' : 'Create + assign'}
          </button>
        ) : null}
        <button type="button" className="unified-secondary-button" onClick={close} disabled={busy}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

// Exported for unit testing (B2-H1 dirty-tracking semantics).
export function ProfileFactsEditorButton({
  contactId,
  facts,
  threadId,
}: {
  contactId: string;
  facts: ThreadDetailDto['contact_profile_facts'];
  threadId: string;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  // Initial value: the operator's own override (or empty when the
  // current displayed value is derived/imported). The hint underneath
  // each field shows what's currently in effect so the operator can
  // see what they'd be replacing if they type.
  const [company, setCompany] = useState(initialManualValue(facts, 'company'));
  const [role, setRole] = useState(initialManualValue(facts, 'role'));
  const [location, setLocation] = useState(initialManualValue(facts, 'location'));
  const [relationshipSummary, setRelationshipSummary] = useState(
    initialManualValue(facts, 'relationship_summary'),
  );

  const [companyDirty, setCompanyDirty] = useState(false);
  const [roleDirty, setRoleDirty] = useState(false);
  const [locationDirty, setLocationDirty] = useState(false);
  const [relationshipDirty, setRelationshipDirty] = useState(false);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Early-out while editing so an upstream refetch doesn't clobber
    // the operator's in-progress typing. When the editor closes
    // (cancel/save) the effect re-runs and resets back to manual-only
    // initial values.
    if (editing) return;
    setCompany(initialManualValue(facts, 'company'));
    setRole(initialManualValue(facts, 'role'));
    setLocation(initialManualValue(facts, 'location'));
    setRelationshipSummary(initialManualValue(facts, 'relationship_summary'));
    setCompanyDirty(false);
    setRoleDirty(false);
    setLocationDirty(false);
    setRelationshipDirty(false);
  }, [editing, facts]);

  const mutation = useMutation({
    mutationFn: (body: {
      company?: string | null;
      role?: string | null;
      location?: string | null;
      relationship_summary?: string | null;
    }) => patchContactProfileFacts(contactId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
      setError(null);
      setEditing(false);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save contact details');
    },
  });

  if (!editing) {
    return (
      <button
        type="button"
        className="unified-inline-link"
        onClick={() => {
          setError(null);
          setEditing(true);
        }}
      >
        Edit
      </button>
    );
  }

  const save = () => {
    // Only PATCH fields the operator actually changed (B2-H1).
    const body: {
      company?: string | null;
      role?: string | null;
      location?: string | null;
      relationship_summary?: string | null;
    } = {};
    if (companyDirty) body.company = emptyToNull(company);
    if (roleDirty) body.role = emptyToNull(role);
    if (locationDirty) body.location = emptyToNull(location);
    if (relationshipDirty) body.relationship_summary = emptyToNull(relationshipSummary);

    if (Object.keys(body).length === 0) {
      // Nothing changed — just close without a network call.
      setEditing(false);
      return;
    }
    mutation.mutate(body);
  };

  const update =
    <T,>(setValue: (v: T) => void, setDirty: (v: true) => void) =>
    (value: T) => {
      setValue(value);
      setDirty(true);
    };

  return (
    <Dialog
      open
      onClose={() => setEditing(false)}
      label="Edit contact facts"
      testId="unified-profile-editor"
      className="unified-facts-popover"
    >
      <FactField
        label="Company"
        value={company}
        onChange={update(setCompany, setCompanyDirty)}
        hint={hintFor(facts, 'company')}
      />
      <FactField
        label="Role"
        value={role}
        onChange={update(setRole, setRoleDirty)}
        hint={hintFor(facts, 'role')}
      />
      <FactField
        label="Location"
        value={location}
        onChange={update(setLocation, setLocationDirty)}
        hint={hintFor(facts, 'location')}
      />
      <FactField
        label="How you know them"
        value={relationshipSummary}
        onChange={update(setRelationshipSummary, setRelationshipDirty)}
        hint={hintFor(facts, 'relationship_summary')}
        multiline
      />
      {error ? <div className="unified-form-error">{error}</div> : null}
      <div className="unified-facts-actions">
        <button type="button" onClick={save} disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving' : 'Save'}
        </button>
        <button
          type="button"
          className="unified-secondary-button"
          onClick={() => setEditing(false)}
          disabled={mutation.isPending}
        >
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

/**
 * Single field row with input + a hint line beneath it showing what
 * the operator would be overriding (when the current value isn't a
 * manual override).
 */
function FactField({
  label,
  value,
  onChange,
  hint,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint: { value: string; source: string } | null;
  multiline?: boolean;
}) {
  // Explicit htmlFor/id (rather than label-wraps-input) so biome's
  // a11y/noLabelWithoutControl rule is satisfied and screen readers
  // see the association reliably.
  const id = useId();
  return (
    <div className="unified-fact-row">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea
          id={id}
          maxLength={1000}
          rows={3}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          maxLength={200}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {hint ? (
        <span className="unified-fact-hint">
          From {hint.source}: <em>{hint.value}</em>
        </span>
      ) : null}
    </div>
  );
}

/**
 * Build the "From X: Y" hint shown beneath an empty input. Returns
 * null when there's no derived/imported value to display (the field
 * is genuinely empty) OR when the current value IS the manual override
 * (already pre-filled, no hint needed).
 */
function hintFor(
  facts: ThreadDetailDto['contact_profile_facts'],
  field: 'company' | 'role' | 'location' | 'relationship_summary',
): { value: string; source: string } | null {
  if (!facts) return null;
  const source = facts.sources?.[field];
  if (!source || source === 'manual' || source === 'empty') return null;
  const value = facts[field];
  if (!value) return null;
  return { value, source: humanSourceLabel(source) };
}

function humanSourceLabel(source: string): string {
  switch (source) {
    case 'linkedin':
      return 'LinkedIn';
    case 'ai':
      return 'AI analysis';
    case 'derived':
      return 'derived';
    default:
      return source;
  }
}

/**
 * Module-level helper so the dep array in `ProfileFactsEditorButton`'s
 * useEffect can stay precise (per-field) without biome flagging a
 * missing-dep warning on an inline closure.
 */
function initialManualValue(
  facts: ThreadDetailDto['contact_profile_facts'],
  field: 'company' | 'role' | 'location' | 'relationship_summary',
): string {
  if (!facts) return '';
  return facts.sources?.[field] === 'manual' ? (facts[field] ?? '') : '';
}

function UnifiedAiCard({ thread }: { thread: ThreadDetailDto }) {
  const analysis = thread.latest_analysis?.structured_output ?? null;
  const importance = thread.contact_profile_facts?.importance_summary ?? null;
  return (
    <section className="unified-side-section unified-side-section-compact">
      <div className="unified-side-kicker">AI</div>
      <h2>Importance</h2>
      {importance ? (
        <>
          <p>{importance}</p>
          {analysis ? (
            <dl className="unified-mini-grid">
              <div>
                <dt>Action</dt>
                <dd>{analysis.suggested_action.replaceAll('_', ' ')}</dd>
              </div>
              <div>
                <dt>Urgency</dt>
                <dd>{analysis.urgency}</dd>
              </div>
              <div>
                <dt>Thread confidence</dt>
                <dd>{Math.round(analysis.confidence * 100)}%</dd>
              </div>
            </dl>
          ) : null}
        </>
      ) : analysis ? (
        <>
          <p>{analysis.ask_summary}</p>
          <dl className="unified-mini-grid">
            <div>
              <dt>Action</dt>
              <dd>{analysis.suggested_action.replaceAll('_', ' ')}</dd>
            </div>
            <div>
              <dt>Urgency</dt>
              <dd>{analysis.urgency}</dd>
            </div>
            <div>
              <dt>Confidence</dt>
              <dd>{Math.round(analysis.confidence * 100)}%</dd>
            </div>
          </dl>
        </>
      ) : (
        <p>No LinkedIn importance signal yet. Fetch the profile or add a manual note.</p>
      )}
    </section>
  );
}

function UnifiedSettingCard({
  title,
  kicker,
  children,
}: {
  title: string;
  kicker: string;
  children: ReactNode;
}) {
  return (
    <section className="unified-page-card">
      <div className="unified-side-kicker">{kicker}</div>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function buildRelationshipTldr(thread: ThreadDetailDto): string | null {
  const chunks = [
    thread.contact_profile_analysis?.summary?.trim() ?? null,
    thread.contact_profile_analysis?.conversation_relevance?.trim() ?? null,
  ].filter((value, index, all): value is string => Boolean(value) && all.indexOf(value) === index);
  if (chunks.length === 0) return null;
  return chunks.join(' ');
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function InboxGlyph() {
  return <NavGlyph path="M4 7h16v11H4z M8 7l2 5h4l2-5" />;
}

function TodayGlyph() {
  // B2 — a dashboard/home grid for the "Today" landing.
  return <NavGlyph path="M4 5h6v6H4z M14 5h6v6h-6z M4 14h6v6H4z M14 14h6v6h-6z" />;
}

function AutomationGlyph() {
  // B3 — an activity/pulse line for the automation log.
  return <NavGlyph path="M3 12h3l2-7 4 14 2-7h7" />;
}

function ClockGlyph() {
  return <NavGlyph path="M12 5a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M12 8v4l3 2" />;
}

function PeopleGlyph() {
  return (
    <NavGlyph path="M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M4 19a5 5 0 0 1 10 0 M17 10a2.5 2.5 0 1 0 0-5 M15 15c2.5 0 4 1.5 4 4" />
  );
}

function GearGlyph() {
  return (
    <NavGlyph path="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M12 3v3 M12 18v3 M3 12h3 M18 12h3 M5.6 5.6l2.1 2.1 M16.3 16.3l2.1 2.1 M18.4 5.6l-2.1 2.1 M7.7 16.3l-2.1 2.1" />
  );
}

// R10 — outreach rail glyphs (modelled on the approved mockup).
function EngageGlyph() {
  return (
    <NavGlyph path="M14 9V5a2 2 0 0 0-2-2l-3 7v11h9.3a2 2 0 0 0 2-1.7l1.4-7a2 2 0 0 0-2-2.3z M5 10H3v11h2z" />
  );
}

function BriefcaseGlyph() {
  return <NavGlyph path="M4 7h16v12H4z M8 7V5h8v2 M4 12h16" />;
}

function ConnectGlyph() {
  return (
    <NavGlyph path="M9 4.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z M3 20a6 6 0 0 1 12 0 M19 8v6 M16 11h6" />
  );
}

function HeartGlyph() {
  return (
    <NavGlyph path="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l8.8 8.8 8.8-8.8a5.5 5.5 0 0 0 0-7.8z" />
  );
}

function NavGlyph({ path }: { path: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
